// Phase 34 (§2/§3) — the ONE shared time-entry table. Renders the tab bar, the
// billability bulk-mark bar, and the rows (pill + per-row actions). Since
// 150 §1 it has ONE mount — CaseTimeEntriesSection — hosted by four views
// (CaseView, CollectiveCaseView, InvoiceView, Dashboard); the kinds callers
// send are "case" | "cc" | "all" (spans-matters). kind="invoice" is still
// HANDLED in the spans-matters arms below but no caller has sent it since
// InvoiceView moved onto the section (verified 155 §2 — the section's own
// union is "case" | "cc" | "all"). The billability MARK model is built ONCE
// here:
//
//   • mark ∈ { unmarked (green) | billable (amber) | unbillable (red) }, an axis
//     INDEPENDENT of the invoiceId binding; the pill shows "Invoiced" (firm
//     colour) whenever invoiceId is set, otherwise the mark.
//   • Marks are written via s(SK.te) → PUT /time-entries/:id; the server derives
//     the `billed` mirror and pushes the row over SSE, so all three tables stay
//     in sync. The client only ever sets `mark` (never `billed`).
//   • The mark bulk buttons render for EVERY user on their own rows (shapes.js
//     limits a non-RP to own rows, so the selection self-limits). The Invoiced
//     tab and the Pro Forma button are gated to RP/Admin/delegate.
//
// All colours use theme tokens (CLAUDE.md §4.9): --gn green, --am amber, --rd
// red, --ac firm/accent.
/* 166 — `import React` is here for `react/react-in-jsx-scope`, which the
   committed eslint config still enforces and which the ratchet counts once per
   JSX element (`scripts/lint/eslint-ratchet.js`: a file's warning count may
   never rise). This brief adds JSX to this file, so the rule fires on every new
   element; putting React in scope is the rule's OWN remedy rather than a way
   around it, it costs nothing at runtime (the automatic JSX transform does not
   need it), and it clears the same debt for every element already here. */
import React from "react";
import CountersStrip, { stripFigures, useEntryFees, shownMoney } from "./CountersStrip"; // 190 round 6 [W] — the strip, once; 213B §2 — its reduction, for the counter
import type { StripFigures, StripHost } from "./CountersStrip";
import type { TFn } from "../lib/taxonomyTypes";
import { useState, useMemo, useRef, useLayoutEffect, useEffect, type RefObject } from "react";
import { useWindowMax } from "../hooks/useWindowMax"; // 193 §D.0 — the window's measure, moved
import { Pencil, Trash2, Archive, FileText, FileDown, Mail, Search, ChevronUp, ChevronDown, User } from "lucide-react";
import { fc, fq, fmin, fd, pname, coIni, SK } from "../utils";
import TypeaheadPicker from "./TypeaheadPicker";
import MenuSelect from "./MenuSelect"; // 151F §2 — the period select's app menu
import FreeDateInput from "./FreeDateInput"; // 213B §15 — the free window's edges
import MatterPicker from "./MatterPicker"; // 150 §3 — the case filter is THE matter picker
import { bulkDeleteTimeEntries, bulkRestoreTimeEntries, bulkRevertTimeEntries, archiveTimeEntry } from "../api";
import type { TeBulkDeleteResult, TeBulkUpdateResult, TeBulkRevert } from "../api";
import { useRoleLabels } from "../hooks/useRoleLabels";
import { SegmentedControl, ScrollHint, useScrollHint, fmtHHMM } from "./SharedUI";
/* 210 §1 — the user filter picks from the collaborators: while they could not
   load, it says so (TypeaheadPicker's `lists`). */
const CO_LIST: readonly string[] = [SK.co];
import { RAD, FS, DIMMED_ROW_OPACITY } from "../lib/tokens"; // 195B §C — the one dimming, which this row never read
// 129 §6 — the two provenance openers. The email one is 126's, unchanged.
import { emailDocUrl, openEmailDocHref } from "../lib/emailLink";
import { useM365Connected } from "../lib/m365Status"; // 131 §10
import { openDocItemHref } from "../lib/docItemLink";
import { openArchivedEmailPdf } from "../lib/emailPdfLink"; // 170 §1d
import { useRefLatest } from "./useConfirmAndUndo"; // 146 §2 — see applyMark
import { periodBounds, PERIOD_OPTIONS, PERIOD_ALL_OPTIONS } from "../lib/periodRange"; // 150 §3 / 174 §3 — one copy of the boundaries AND of the offer
import type { PeriodValue } from "../lib/periodRange";
import Table from "./Table";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

/* 129 §6 — which matter a row's provenance belongs to, read off the ROW. The
   spans-matters surfaces (kind="all" today; "invoice" when this was written)
   render entries that span matters, and a drive item
   id is meaningless without the matter whose folder resolves its drive. `caId`
   is `timeEntries.js`'s own alias for `caseId` and both are accepted, which is
   the drift 125 §2 ruled on: read every spelling, write one. */
type ProvenanceRow = { ccId?: number | null; caId?: number | null; caseId?: number | null };
const _teMatter = (e: ProvenanceRow | null | undefined) =>
  e?.ccId != null ? { ccId: e.ccId } : { caseId: e?.caId ?? e?.caseId ?? null };

/* 146 §2 — the two shapes the mark undo passes around. Narrow on purpose: this
   component's props are `any`, so an annotation is the only thing that types
   these callbacks at all, and the `any` ratchet is at this file's ceiling —
   reaching for `(e: any)` to do it is the decay that counter exists to stop. */
type TeRow = {
  id: unknown; invoiceId?: unknown; mark?: string; billabilityMark?: string;
  desc?: string | null;
  // 146 §5 — the fields the sortable columns read.
  date?: string | Date | null; coId?: number | null; type?: string | null; min?: number | null;
};
type MarkSnap = { id: unknown; mark: string };

/** 213 addendum 3 §18 [RULED 38] — the rows on screen, counted, and their
 *  minutes summed. Takes `unknown`: it is the seam where the table's untyped
 *  rows are read (pluralVars' way), so the counter adds no unsafe read. */
function shownOf(rows: unknown): { n: number; min: number } {
  if (!Array.isArray(rows)) return { n: 0, min: 0 };
  let min = 0;
  for (const e of rows as TeRow[]) min += typeof e.min === "number" ? e.min : 0;
  return { n: rows.length, min };
}

/* 150 §4 — the two shapes the counters read. Narrow on purpose, the TeRow
   argument: this file's props are `any` and the ratchet is at its ceiling. */
type FrozenRow = TeRow & {
  invoiceId?: number | null;
  invoicedRate?: number | null;
  invoicedAmount?: number | null;
};

/* 146 §5 — the sortable columns, RULED: DATE, QUI, TYPE, TEMPS, TAUX, MONTANT.
   Not DESCRIPTION — a description sort answers no question anybody asks. */
type SortKey = "date" | "who" | "type" | "min" | "rate" | "amt";
const SORT_ARROW = { verticalAlign: "middle", marginLeft: 2 };

export const TE_MARK = { UNMARKED: "unmarked", BILLABLE: "billable", UNBILLABLE: "unbillable" };
export const markOf = (e: any) => e?.mark || e?.billabilityMark || TE_MARK.UNMARKED;

// Phase 35 Stage-3 r2 (§3) — hardcoded English fallback for the invoiced-entry
// lock tip. Guarantees the tooltip is NON-EMPTY even if the te.invoiced_tip
// translation seed never ran on an environment (fr/nl come from the seeded key
// when present; this literal is the en fallback and the last-resort default).
const INVOICED_TIP_EN = "Already invoiced — cancel the invoice to correct";

// Pill colour recipe per state (theme tokens only).
const PILL = {
  unmarked:   { bg: "rgba(52,211,153,.12)",  fg: "var(--gn)", bd: "rgba(52,211,153,.35)", dim: false },
  billable:   { bg: "rgba(251,191,36,.12)",  fg: "var(--am)", bd: "rgba(251,191,36,.35)", dim: false },
  unbillable: { bg: "rgba(248,113,113,.10)", fg: "var(--rd)", bd: "rgba(248,113,113,.35)", dim: true },
  invoiced:   { bg: "var(--as)",             fg: "var(--ac)", bd: "var(--bd)",             dim: false },
};

/* 195 §A.5 — THE MARK PILL, MOVED to module scope (the closure `pill` below
   became this mount) so the Archive's time-entry LINE can show the matter's
   own mark column: Marquer / Facturable / Non facturable / Facturé. Same
   markup, same colours, same words; hookless (Bug-A). */
type PillRow = { invoiceId?: unknown; invoiceNumber?: string | null; mark?: string; billabilityMark?: string };
export function TeMarkPill({ e, t }: Readonly<{ e: PillRow; t: TFn }>) {
  const key = e.invoiceId ? "invoiced" : markOf(e);
  const p = (PILL as Record<string, typeof PILL.unmarked>)[key] ?? PILL.unmarked;
  const label = e.invoiceId
    ? t("te.mark.invoiced", { fallback: "Invoiced" })
    : key === "billable" ? t("te.mark.billable", { fallback: "Billable" })
    : key === "unbillable" ? t("te.mark.unbillable", { fallback: "Unbillable" })
    : t("te.mark.unmarked", { fallback: "Mark" });
  return (
    <span className="tag" style={{ background: p.bg, color: p.fg, border: "1px solid " + p.bd, opacity: p.dim ? 0.6 : 1 }} title={e.invoiceId ? (e.invoiceNumber ?? "") : ""}>
      {label}
      {!!e.invoiceId && !!e.invoiceNumber && <span style={{ fontSize: FS.s8, marginLeft: 3, opacity: 0.8 }}>{e.invoiceNumber}</span>}
    </span>
  );
}

function fmtHM(min: any) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h}h${r ? " " + r + "m" : ""}` : `${r}m`;
}

export default function TimeEntryTable({
  kind,                 // 'case' | 'cc' | 'invoice' | 'all' (150 §1 — spans matters)
  entries = [],         // App-global `te` already scoped to this matter
  cs = null,            // the matter object (case/cc); spans-matters kinds resolve per-row
  scopedCaseFor,        // (entry) => case object [invoice/all kinds, entries span matters]
  co = [], tkTypes = [],
  canTerms = false, canDelegate = false,
  te = [], sTe, s, SK,  // sync engine
  onEdit, onDelete, onProForma, onDoubles,
  // 144 §4 — the bulk bar's edit control (ruling 3: this bar and nowhere
  // else). canBulkEdit is the host's UI HINT — admin or (bulkEdit bit-or-grant
  // AND RP of this matter), the canMarkBilled shape; the server re-judges per
  // row either way. onBulkEdit opens the canonical om("teBulk") modal with the
  // selected ids — and, since 145 §1, with the completion callback the modal
  // hands its result back through, so the undo window opens on THIS host's
  // single slot rather than App's (see bulkEditDone).
  canBulkEdit = false, onBulkEdit,
  // 144 §5 — bulk delete rides the HOST's confirmAndUndo (one single-slot
  // pending-undo per host is the hook's contract; a second hook instance here
  // would let a bulk window and a single-row window coexist and neither would
  // close the other early). canBulkDelete is the same hint shape as
  // canBulkEdit, on the bulkDelete key.
  canBulkDelete = false, confirmAndUndo,
  // 150 §5 (g) — the stand-alone pill-column right; see showPill below.
  canPillColumn = false,
  t, toast,
  locked = true,        // CONTROLLED by the host view (gates its own buttons too)
  toolbarExtra = null,  // host buttons on the tab-strip row's far right (151C §1: Import/Export)
  /* 151C §1 — Invoicing's FIFTH row: Create pro forma far left, the dedup
     pair far right, unlocked only. When set, the pro forma button leaves the
     bulk bar (row 4 is edit/mark/delete there) for the fifth row, where it no
     longer needs a selection to be VISIBLE — an empty click names what it
     needs instead of hiding. */
  proFormaRow = false,
  /* 151C §2 (iii) — content that FOLLOWS the entries inside the scroll
     window (Invoicing hands its pro forma list; reaching it = scroll to
     bottom, accepted by the ruling). Locked, it renders in plain flow after
     the table — the position it holds today. */
  afterTable = null,
  // 150 §3 — the user filter's recents store is per-viewer, and the Dashboard
  // surface opens narrowed to the viewer ("My time" is a DEFAULT of this
  // filter, not a different structure).
  currentUser = null,
  initialUserFilter = null,
  // 150 §3 — whatever the host holds for the firm's week: the resolved axis
  // (CV/CCV/Dashboard) or the settings object (InvoiceView); periodBounds
  // resolves either, and absence falls back to the Mon–Fri constants.
  schedAxis = null,
  /* 150 §3 — the case filter renders only where the host says the population
     SPANS matters (Dashboard). CV and CCV hide it by construction (one case
     there); on Invoicing the existing scope control at the top of the view IS
     the case narrowing — it chooses which matter's rows this table is handed
     at all — and a second control over the same axis inside the row is
     exactly what "lift it rather than writing a second" forbids. `ca`/`cc`
     feed the picker's population where it does render. */
  showCaseFilter = false,
  ca = [],
  cc = [],
  // 150 §1 — the Dashboard surface opens on the work week (the card it
  // replaces was hard-filtered to the week; a DEFAULT of the period filter,
  // not a different structure).
  initialPeriod = null,
  /* 150 §4 — the seven counters. `canSeeCounters` is InvoiceView's
     canSeeCards GENERALISED (admin, or RP of the matter(s) in scope) and the
     host computes it for its own scope; when false the sums are NEVER
     COMPUTED, not computed-and-hidden (§4.2: a client that holds seven and
     displays four is one refactor from leaking three). `inv` is the viewer's
     invoice list, for the two invoice-level cards. */
  canSeeCounters = false,
  inv = null,
  /* 158 §3 — the strip follows the lock (151C's overrule of 150 §2) on every
     surface that HAS something to unlock. The Invoices section has not: there
     is no editing behind its lock and the figures are the point of the
     screen, so its host says so explicitly. NOT inferred from `kind` — that
     is `all | cc | case` and InvoiceView passes `cc` or `case` like CV and
     CCV do, so inferring would silently unpin CV and CCV too. */
  countersAlwaysVisible = false,
  /* 151D §2(e) — the counters pane's opaque plate, per surface: the host
     whose list sits on the card colour (Dashboard) hands `var(--bc)` so the
     pane reads as the card's own floor; everywhere else the standing shade. */
  counterBg = "var(--bd)",
}: any) {
  /* 131 §10 — 126 §7's RULING IS NOT SURFACE-SPECIFIC. "When M365 is not
     connected at all, the affordance is ABSENT, not present-and-refusing."
     `m365Status.ts` exists precisely to be asked once and shared, and this
     table was not asking: #435's screenshot showed a time-entry mail glyph
     PRESENT and refusing with `(not_connected)` on staging, where M365 is
     never connected by standing ruling. Both halves of the glyph resolve
     through Graph — the email through `/emails/doc-url`, the document through
     `/documents/item-link/:itemId` — so both are gated, not just the one in
     the screenshot. `=== true` and not `!== false`: `null` means the answer is
     still in flight, and a control that is missing beats one that lies. */
  const m365 = useM365Connected();
  // 151C §2 (iv) — the ruled scroll-affordance idiom, reused not reinvented:
  // the modals' own hook, riding the entry window below.
  const teWin = useScrollHint();
  /* ── 151D §1(f)+(h) / §2(c)(e) — THE WINDOW IS SIZED BY MEASUREMENT, and
     the 151C allowance is retired on one: driven at 1280×800/zoom 1.2307,
     the −270px guess overshot by ~34 layout px, so at max page scroll the
     section title sat 30 visual px ABOVE the viewport — the stack never
     pinned. The end state is exact by construction instead: the window gets
     `scroller.clientHeight − tail − overhead`, where `overhead` is the
     section's rows above the window (sec minus win, both offsetHeight — the
     window's own box cancels, so the formula is a fixed point) and `tail` is
     everything the page holds BELOW the section (the .content padding, a
     Dashboard card's own). Section + tail then fill the viewport exactly,
     and — the te section being the LAST section on every surface — the page
     bottoms out precisely when the title row reaches the top: title, tab
     strip, filters, bulk row and headers stand still, the entries scroll.
     offsetHeight/scrollTop/clientHeight are LAYOUT px (zoom-free); the one
     rect subtraction is visual and is divided by the root zoom whole —
     142 §1's rule. `hintInsets` lifts the up arrow off the pinned header
     row; the down inset is 0 since 190 round 8 [AK] — the counters plate is
     no longer inside the window (it is App's footer slot, CountersStrip.tsx),
     so nothing of the window's own bottom edge is covered. The scroller's
     clientHeight shrinks by the plate's height as a flex sibling, which the
     ResizeObserver on the scroller below already sees. */
  /* 158 §3 CORRECTED — THE STRIP IS THE WINDOW'S LAST CHILD, SO IT NEEDS THE
     WINDOW. `position: sticky; bottom: 0` resolves against the nearest
     scrolling ancestor, and this window is it — but the scroller styles below
     were applied on `!locked` alone. Pinning the strip open in a LOCKED
     Invoices section therefore left it with no scroller of its own, so it
     resolved against the PAGE and floated over the pro forma list beneath the
     section instead of being the floor the entries pass under.

     One predicate, used everywhere the window is decided: the same code path
     CV and CCV already take on unlock, taken here for the same reason — a
     visible strip means a windowed list. No new mechanism, and the counters
     gate itself (`countersAlwaysVisible || !locked`) is unchanged. */
  const windowed = !locked || !!countersAlwaysVisible;
  /* 193 §D.0 — the measure that stood here, line for line, is
     hooks/useWindowMax.ts now (a MOVE, so the pro forma list can size its own
     window by the same arithmetic — 193 §D). Same refs, same section
     selector, same re-measure key; `tableRef` is declared below and read
     inside the effect only, as before. */
  const tableRef = useRef<any>(null);
  const { winMax, hintInsets } = useWindowMax({
    enabled: windowed,
    win: teWin.ref as RefObject<HTMLElement | null>,
    table: tableRef as RefObject<HTMLTableElement | null>,
    sectionSelector: ".csSec-time",
    attachKey: canSeeCounters,
  });
  const [sel, setSel] = useState(() => new Set());
  // 146 §3, RULED: the list opens on what still needs a decision, not on
  // everything ever logged. The other tabs stay one click away, and a matter
  // with nothing unmarked opens empty — which is the ruling's own answer to
  // that case, not an oversight: nothing needs a decision.
  const [tab, setTab] = useState("unmarked");
  /* 147 §1.5 — THE THREE NARROWINGS ARE DECLARED TOGETHER, and the move is
     forced rather than tidy: the anchor's layout effect below lists all three
     in its dependency array, and a `const` referenced above its declaration is
     a temporal-dead-zone throw, not a stale read. Each one keeps its own
     explanation down at the memo that consumes it — `q` at the description
     search (146 §4), `act` at the activity filter (147 §1.2). */
  const [q, setQ] = useState("");
  const [act, setAct] = useState("");
  /* 150 §3 — the new narrowings declare beside the three they join (147 §1.5:
     the anchor's dep array may not name a `const` below it). `userF` is a
     collaborator id or null — null narrows nothing, like the empty search.
     `period` is one of the five ruled options ("" narrows nothing; "free"
     carries its own from/to and narrows only once an edge is typed). */
  const [userF, setUserF] = useState<number | null>(initialUserFilter ?? null);
  const [caseF, setCaseF] = useState<{ type: "ca" | "cc"; id: number } | null>(null);
  const [period, setPeriod] = useState<PeriodValue>(initialPeriod || { kind: "" });

  // Phase 34 (Stage 3, round 2) — keep the Time Entries section VISUALLY ANCHORED
  // across a tab change. Switching tabs changes the row count, so the section's
  // height changes; the browser then clamps the scroll to the new (shorter) page
  // end, which reads as a jump. Saving/restoring the scroll cannot fix this once
  // the page is a different height. Instead we keep the page from shrinking under
  // the current viewport: a bottom SPACER is over-reserved to the table's
  // pre-switch height (so the reflow itself can never clamp), then a layout
  // effect trims it to the exact shortfall needed to keep the spot at the
  // viewport bottom reachable — zero in the common mid-page case (no gap), only
  // the deficit when the section sits at the very bottom of the page.
  // 193 §D.0 — `tableRef` is declared above the window's measure now (it is
  // an argument there, no longer a read inside the effect); the anchor's own
  // refs stay here.
  const anchorRef = useRef<any>(null);
  const spacerRef = useRef(0);
  const [spacer, setSpacer] = useState(0);

  const findScrollParent = () => {
    /* 190 round 7 [AE] — THE ANCHOR IS THE PAGE'S, NEVER THE WINDOW'S. This
       climb used to start at the table's parent, which since 151C §2 is the
       entry WINDOW — `overflow: auto`, capped at `winMax` — and whenever the
       list overflowed it, the window itself was taken as the scroll parent.
       The reservation then measured the WINDOW's deficit (its own visible
       height minus its shortened content) instead of the page's, so every
       narrowing from a long list to a short one left the window at its cap
       with a blank band inside it — MEASURED on staging (Smoke Case 01, 1600
       × 1000, zoom 1): Tous (23 rows, window 796) → Facturable (5 rows): the
       spacer 538 px, the last row 536 px above the pinned strip, with the
       page scrolled MID-WAY (scrollTop 900 of a possible 1028 after the
       shrink — nothing to clamp, nothing to anchor). Phase 34's own contract
       is "zero in the common mid-page case, only the deficit when the section
       sits at the very bottom of the page", and that is a fact about the PAGE
       scroller; starting the climb above the window keeps the window out of
       it. Unwindowed, the same div is `overflow: visible` and the climb
       passed through it anyway — byte-identical result. */
    let el: HTMLElement | null = teWin.ref.current ? teWin.ref.current.parentElement : (tableRef.current ? tableRef.current.parentElement : null);
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  /* ── 147 §1.5 — THE ANCHOR TAKES A NARROWING, NOT A TAB. A REPAIR OF 146 §4 ──
     Reported from the running app: the list "jumps around when searched".

     THE SORT IS NOT THE CAUSE, and that was checked before anything was
     touched. `visible` (:below) sorts a COPY, never the memo it was handed;
     `Array.prototype.sort` has been stable since ES2019; and a row with no
     answer sorts LAST in BOTH directions, so no row crosses the list when the
     direction flips. Driven as well as read — see the PR body.

     The cause is this anchor, which Phase 34 built for exactly this defect and
     which 146 §4 did not reach. Narrowing the list shortens the page, the
     browser clamps the scroll to the new end, and the section jumps. 146 added
     a SECOND narrowing — the search — and wired it straight to `setQ`, so the
     tab change was anchored and typing was not. 147 §1.2 then added a third.

     So the anchor stops being about tabs: `narrow(apply)` takes ANY state
     change that shortens the list, and the three call sites hand it their own
     setter. §3's period selector is the fourth and lands on it unchanged.

     ITS DEPENDENCY LIST BECAME `[tab, q, act]`, and that is stated rather than
     stumbled into: the layout effect now fires on EVERY KEYSTROKE, because
     every keystroke IS a narrowing and each one shortens the page by a
     different amount. It is cheap — two reads and a `setSpacer` that returns
     the same number in the common mid-page case — and it is the only key that
     matches when the trim must run. */
  /* ── 148 §2 — THE RESERVATION MAY NEVER REDUCE THE PAGE, AND THE SCROLL IS
     PUT BACK RATHER THAN INFERRED. A REPAIR OF 147 §1.5 ────────────────────
     Reported after §1.5 shipped: "still jumps, bottom of the list seems to be
     the reference."

     `spacerRef` CARRIES ONE MEANING AT EVERY READ — the spacer height that is
     rendered right now (or, immediately after a write, the one about to be).
     It is read in `narrow` as "what is on screen" and in the effect as "what
     the reservation put on screen", and both are that same fact.

     TWO CHANGES, AND ONLY THE SECOND ONE FIXES WHAT WAS MEASURED:

     (1) THE RESERVATION IS `spacerRef.current + tOld`, NOT `tOld`. As written
     it guaranteed only `natural_new + tOld >= natural_old` — the page could
     not fall below its height WITHOUT the spacer. When a spacer was already
     rendered (which is precisely the at-the-page-bottom case this whole
     mechanism exists for) the guarantee fell short by exactly that spacer, so
     from the second narrowing on, `setSpacer(tOld)` was a REDUCTION: measured
     at 1514 CSS px on the fourth keystroke of a 140-row list.
     THAT REDUCTION DOES NOT CLAMP, AND THE BRIEF'S HYPOTHESIS THAT IT DOES IS
     REFUTED — see the commit body: keystrokes 2, 3, 4 and 5, clearing the box
     and the activity filter all hold the page and the section, at every text
     size. The intermediate render is never painted. It is corrected anyway
     because it is what makes (2) sound: with the sum reserved, the page at the
     moment the effect runs can never be shorter than the page the scroll was
     captured on.

     (2) THE SCROLL IS RESTORED, and this is the one that moves the needle.
     Reserving height only makes the old position REACHABLE; it does not put
     the viewport back if something moved it, and something does — measured, on
     the FIRST narrowing only, 49.6 CSS px with the search box on screen and
     597.2 with the section below the fold, in a single scroll event fired
     after the page had already reached its final height. Not a clamp (the
     landing point is nowhere near the new maximum) and not scroll anchoring
     (`overflow-anchor: none` changes it by 0.0px). So it is not diagnosed from
     here — it is simply undone: the position is captured with `anchorBottom`
     and put back inside the effect, WHILE THE OVER-RESERVATION IS STILL IN THE
     DOM. That ordering is the whole trick, and it is why (1) is needed: at
     that instant the page is at its tallest, so the write cannot be clamped,
     and once the trim commits the page is exactly `anchorBottom` tall — whose
     maximum scroll IS the restored position. Nothing can clamp it afterwards
     either. */
  const narrow = (apply: () => void) => {
    const sc = findScrollParent();
    anchorRef.current = { sc, scrollTop: sc.scrollTop, anchorBottom: sc.scrollTop + sc.clientHeight };
    const tOld = tableRef.current ? tableRef.current.offsetHeight : 0;
    // The rendered spacer PLUS the table that is about to shrink: a sum, so the
    // page can only ever grow here. A `max()` would not do — the list may lose
    // its whole height, and `max(tOld, spacer)` still falls short by `tOld`.
    const reserve = spacerRef.current + tOld;
    spacerRef.current = reserve;
    setSpacer(reserve);
    apply();
  };
  const onTabChange = (k: any) => narrow(() => setTab(k));

  useLayoutEffect(() => {
    const a = anchorRef.current;
    if (!a) return;
    anchorRef.current = null;
    /* 190 round 7 [AE] — INSIDE THE WINDOW THE SPACER IS CAPPED, so its
       contribution to the PAGE is not its own height. The window is
       `min(winMax, content + spacer)` tall: with the over-reservation in the
       DOM the page has grown by `window − min(window, content)` and not by
       `reserve`. Subtracting `reserve` here read the page as SHORTER than it
       is without any spacer and manufactured a deficit mid-page (measured: 99
       px on the 23 → 5 narrowing above). Uncapped (the window shorter than its
       cap, or the section unwindowed in page flow) the expression is exactly
       `spacerRef.current` — the line that stood here. And the spacer that
       closes a page deficit of `need` is `need` itself in both cases: a
       deficit exists only while the content is under the cap, and it is
       bounded by the window's own shrink (`windowBefore − content`), so
       `content + need` never exceeds the cap. */
    const winEl = teWin.ref.current;
    const contributed = windowed && winEl
      ? winEl.offsetHeight - Math.min(winEl.offsetHeight, winEl.scrollHeight - spacerRef.current)
      : spacerRef.current;
    const natural = a.sc.scrollHeight - contributed; // page height without our spacer
    const need = Math.max(0, a.anchorBottom - natural); // 0 unless the section is at the page bottom
    // Before the trim, never after: the over-reservation is still in the DOM,
    // so the page is at its tallest and this write cannot be clamped away.
    if (Math.abs(a.sc.scrollTop - a.scrollTop) > 0.5) a.sc.scrollTop = a.scrollTop;
    spacerRef.current = need;
    setSpacer(need);
  }, [tab, q, act, userF, caseF, period]);

  // 150 §1 — 'all' joins 'invoice' on every spans-matters arm: the matter is
  // resolved per row, never off `cs`.
  const spansMatters = kind === "invoice" || kind === "all";
  const caseOf = (e: any) => (spansMatters ? (scopedCaseFor ? scopedCaseFor(e) : null) : cs);

  // C.2 — the per-row Rate/Amount money is server-handed (POST
  // /api/financials/entry-fees; same arithmetic, verbatim,
  // recording-verified: recordings/c2-entryfees/). The table posts the rows
  // it holds (reduced via src/lib/financialsClient.ts) and renders what it
  // is handed — it computes none of the money. The request is keyed by its
  // own reduced wire body: hosts pass `entries` as a fresh array identity
  // every render, so a JSON signature (not array identity) keeps the effect
  // from re-posting. Latest-wins; results are keyed by entry id so a row
  // never wears another row's money; fq(null)/"—" placeholders before the
  // first response.
  const { rows: _roleRows } = useRoleLabels();
  /* 190 round 6 [W] — the POST /financials/entry-fees effect lives in
     CountersStrip.tsx's `useEntryFees` (the firm host posts the same body
     over the firm's rows): the per-row Rate/Amount money is server-handed,
     keyed by row id, "—" placeholders before the first response. */
  const feeCalc = useEntryFees({ entries, caseOf, co: co || [], roleRows: _roleRows });

  // Whether the Invoiced tab/affordances are available: RP/Admin/delegate OR the
  // server actually delivered invoiced rows (it only does so to those viewers).
  const hasInvoiced = useMemo(() => entries.some((e: any) => e.invoiceId), [entries]);
  const showInvoicedTab = canDelegate || hasInvoiced;

  const counts = useMemo(() => {
    const c = { all: entries.length, unmarked: 0, billable: 0, unbillable: 0, invoiced: 0 };
    for (const e of entries) {
      if (e.invoiceId) { c.invoiced++; continue; }
      const m = markOf(e);
      if (m === TE_MARK.BILLABLE) c.billable++;
      else if (m === TE_MARK.UNBILLABLE) c.unbillable++;
      else c.unmarked++;
    }
    return c;
  }, [entries]);

  /* ---- 147 §1.4(b) — A ROW MARKED INSIDE THE UNDO WINDOW HOLDS ITS PLACE ----
     REPAIR OF 146 §2, reported from the running app: mark rows billable on
     *Non marqué* and the toast offers Undo while the screen shows nothing to
     undo — the rows are gone the instant the write lands.

     `useConfirmAndUndo` runs `onConfirm` SYNCHRONOUSLY (`:97` — the window
     offers the reversal, it does not defer the write), so `mark` changes at
     click time and `tabFilter` below stops matching on every tab but *Tout*.
     RULED: the row stays where it is until the window closes. It wears its NEW
     pill immediately — that is what makes the act legible — and it slides out
     only when the undo expires or is displaced.

     NO TIMER IS STARTED HERE and nothing new is invented: the hook owns the
     clock and already provides both edges, and `applyMark` was using neither.
     `onExpire` is "the COMMIT point: the window closed without undo", fired on
     expiry, on displacement by another undoable act, and on unmount; the Undo
     click is `undo.restore`. This set is filled at click time and cleared by
     BOTH, so it can only ever outlive the window by nothing.

     IT CARRIES THE TAB IT WAS MARKED ON, not just the ids. A bare id set would
     admit a row marked BILLABLE onto *Non facturable* as well — every tab, not
     the one the user is looking at. One tab is enough because the hook is
     single-slot: at most one mark window exists at a time.

     THE COUNTERS ARE DELIBERATELY NOT SPECIAL-CASED. `counts` (:224) reads
     `entries`, so *Non marqué* drops 405 → 401 the moment the write lands while
     the four rows sit there under their new colour. That is the feedback, not a
     bug: the number says what is true, the rows say what you just did. */
  const [justMarked, setJustMarked] = useState<{ tab: string; ids: Set<unknown> } | null>(null);

  /* ── 151C §4 — THE AMBER PULSE, 150's owed piece, VERIFIED NEVER BUILT
     (no pulse in this file, no amber keyframe in App.css, no commit names
     one) and built here as ruled: when a mark-undo window CLOSES WITHOUT
     UNDO, the held rows leave the tab they were parked on (§1.4(b) above)
     and arrive on their destination tab — which pulses amber briefly so the
     departure reads as a move, not a loss. COMMIT EDGE ONLY: an Undo puts
     the rows back where the eye already has them, so nothing arrives and
     nothing pulses. The class is one-shot (×4, 4.8s — 151D §3(a) doubled
     151C's ×2, 2.4s); the state clears just after so a later commit to the
     same tab restarts the animation. */
  const [pulseTab, setPulseTab] = useState<string | null>(null);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firePulse = (destTab: string) => {
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    setPulseTab(destTab);
    // 151D §3(a) — 2600 → 5000: the doubled 4.8s animation + the same clear margin.
    pulseTimer.current = setTimeout(() => setPulseTab(null), 5000);
  };
  useEffect(() => () => { if (pulseTimer.current) clearTimeout(pulseTimer.current); }, []);

  /* ── 150 §2 — THE GROUPS, wired to the keys the hosts already hand down ──
     (b) marking  = canDelegate (markBilled): the strip AND the mark buttons.
     (c) modification = canBulkEdit | canBulkDelete (their own keys) + dedups.
     (f) selection is DERIVED — (b) OR (c), never its own key: checkboxes with
     nothing to act on are furniture.
     NO STRIP → THE LIST SHOWS ALL: 146 §3's unmarked default is a DECISION
     tab, and a viewer without the mark key may not be parked on a decision
     they may not take — `effTab` forces 'all' whenever the strip is absent,
     so a locked or keyless viewer is never silently looking at one slice
     with no label and no way out.
     150 §5 — (g) ARRIVED: the pill column shows whenever (b) or (c) is on
     (you cannot mark what you cannot see) OR when its own key grants it
     alone, read-only. Before the key existed the column was unconditional,
     so this is the ruled right to HIDE landing, and an ungranted ordinary
     collaborator loses the pills until a role or grant says otherwise. */
  const canMark = !!canDelegate;
  const canModify = !!(canBulkEdit || canBulkDelete);
  const showSelect = !locked && (canMark || canModify);
  const showPill = canMark || canModify || !!canPillColumn;
  const effTab = canMark ? tab : "all";

  const tabFilter = (e: any) => {
    if (effTab === "all") return true;
    if (effTab === "invoiced") return !!e.invoiceId;
    if (e.invoiceId) return false;
    // After the invoiced refusal, never before it: a row invoiced by someone
    // else INSIDE the window still leaves, for the same reason the undo would
    // refuse to re-mark it (§7).
    if (justMarked && justMarked.tab === effTab && justMarked.ids.has(e.id)) return true;
    return markOf(e) === effTab; // 'unmarked' | 'billable' | 'unbillable'
  };
  // ---- 146 §4 — THE DESCRIPTION SEARCH ----
  //
  // RULED: it matches the DESCRIPTION and nothing else. Not the person, not the
  // activity code — those are columns you sort by, not text you hunt through.
  // Case-insensitive, matches anywhere in the string, an empty box means no
  // narrowing. Nothing clever: no fuzzy matching, no tokenising.
  //
  // IT NARROWS `visible`, AND THAT IS WHY SELECT-ALL FOLLOWS IT — a property of
  // the wiring rather than a promise about it. `selectableVisible` is `visible`
  // minus invoiced rows (:below), `toggleAll` builds its Set from
  // `selectableVisible`, and `allVisibleSelected` reads the same array, so the
  // header checkbox can only ever tick rows that are on screen. The chain is
  // driven and checked in the PR body rather than argued from here.
  //
  // The list is built in ONE named step per narrowing, because the sort is the
  // END of the chain and the sorted set is what `visible` becomes. Any other
  // order and select-all stops agreeing with the screen.
  //
  // 150 §4 — THE TAB MOVED TO THE END OF THE FILTERS: `entries → search →
  // activity → user → case → period → TAB → sort`. The seven counters follow
  // the FILTERED rows exactly and never the tab — a tab picks one mark-state
  // slice, and cards that partition BY mark-state would zero six of themselves
  // on every tab but Tout — so the chain needs its pre-tab stage as a named
  // set (`filtered` below). Every predicate in the chain is a pure row test,
  // so the reorder commutes: `visible` is the same set in the same order as
  // before, and select-all still reads exactly the screen.
  const searched = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return entries;
    return entries.filter((e: TeRow) => (e.desc || "").toLowerCase().includes(n));
  }, [entries, q]);

  // ---- 147 §1.2 — THE ACTIVITY-TYPE FILTER, BESIDE THE SEARCH ----
  //
  // THE OPTIONS ARE READ, NEVER TYPED. They come from the `activity_code`
  // taxonomy, which is per-tenant and which firms edit — the prod deploy
  // carries 17 rows differing from canon — so a list written into this file
  // would be wrong for every firm that has touched Settings → Taxonomies. The
  // taxonomy is already here: `tkTypes` is App's own
  // `useTaxonomyList("activity_code")` (App.tsx:1452), threaded to this
  // component by both hosts, and `typeCell` below already reads it. No second
  // fetch, no second cache, no hardcoded list.
  //
  // AND THE LIST IS A UNION, NOT A SELECTION (rule 2). `useTaxonomy` drops
  // archived rows (`useTaxonomy.ts:274`), so a firm that retires an activity
  // code leaves behind entries the taxonomy no longer names — and a filter
  // that cannot name them is a filter with rows it can never reach. So the
  // option list is the taxonomy's codes PLUS any code the rows in front of
  // the user actually carry. A retired code shows as its bare code, which is
  // what the TYPE column prints for it anyway.
  //
  // IT NARROWS `visible`, WHICH IS WHY SELECT-ALL AND THE SORT FOLLOW IT.
  // Same property 146 §4 relied on and the same reason: `selectableVisible`
  // is `visible` minus invoiced rows, `toggleAll` builds its Set from
  // `selectableVisible`, the sort is the LAST step of the chain and the sorted
  // set is what `visible` becomes. The brief asked for that to be verified
  // rather than assumed, and it is driven — see the PR body.
  //
  // ONE NARROWING PER NAMED STEP: `entries → tab → search → activity → sort`.
  // `act` is declared beside `tab` and `q` (:133); see 147 §1.5's anchor.
  /* 151C §1(b) — the option shape is TypeaheadPicker's ({key,label}): the
     control stops being a native select and becomes the same autocomplete as
     the user filter, opening on this COMPLETE list. The population is
     unchanged — taxonomy codes plus any code the rows carry. */
  const actOptions = useMemo(() => {
    const out: { key: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const x of (tkTypes || []) as { code?: string; desc?: string }[]) {
      const c = typeof x?.code === "string" ? x.code : "";
      if (!c || seen.has(c)) continue;
      seen.add(c);
      out.push({ key: c, label: x.desc ? `${c} — ${x.desc}` : c });
    }
    for (const e of entries as TeRow[]) {
      const c = typeof e.type === "string" ? e.type : "";
      if (!c || seen.has(c)) continue;
      seen.add(c);
      out.push({ key: c, label: c });
    }
    return out;
  }, [tkTypes, entries]);
  const typed = useMemo(() => {
    if (!act) return searched;
    return searched.filter((e: TeRow) => (e.type || "") === act);
  }, [searched, act]);

  // ---- 150 §3 — THE USER FILTER, AFTER THE ACTIVITY ONE ----
  //
  // The chain gains a step: `entries → tab → search → activity → user → sort`,
  // in that order because the sort must stay LAST — the sorted set is what
  // `visible` becomes, and `selectableVisible`/`toggleAll`/`allVisibleSelected`
  // all read `visible`, which is the wiring that keeps select-all agreeing
  // with the screen (146 §4's property; §3 asks for it to be re-verified and
  // the chain is the verification — every narrowing sits ABOVE the sort and
  // nothing below `visible` reads an unnarrowed set).
  //
  // THE OPTION LIST IS A UNION, NOT A SELECTION (rule 2, actOptions' own
  // argument): the collaborator roster PLUS any coId the rows actually carry —
  // a collaborator who left the firm still has rows, and a filter that cannot
  // name them is a filter with rows it can never reach. A rosterless id shows
  // as "#id", which is what the QUI column prints for it anyway ("—" there,
  // but the filter needs a nameable row).
  //
  // RECENTS FIRST, THEN AUTOCOMPLETE — MatterPicker's shape (TypeaheadPicker
  // since 150 §3's extraction), pointed at collaborators. The recents are the
  // viewer's own last picks, in `lx:recentTeUsers:<viewer>` — localStorage is
  // UI preference, the posture every `lx:` key that survived Phase 30 has.
  const userOptions = useMemo(() => {
    const out: { key: string; label: string; mono?: string; id: number }[] = [];
    const seen = new Set<number>();
    for (const c of (co || []) as { id?: number }[]) {
      if (typeof c.id !== "number" || seen.has(c.id)) continue;
      seen.add(c.id);
      out.push({ key: String(c.id), id: c.id, label: pname(c), mono: coIni(c) });
    }
    for (const e of entries as TeRow[]) {
      if (typeof e.coId !== "number" || seen.has(e.coId)) continue;
      seen.add(e.coId);
      out.push({ key: String(e.coId), id: e.coId, label: `#${e.coId}` });
    }
    return out;
  }, [co, entries]);
  const RECENT_USERS_KEY = `lx:recentTeUsers:${currentUser ?? "anon"}`;
  const recentUserOptions = useMemo(
    () => () => {
      let ids: unknown[] = [];
      try {
        ids = JSON.parse(localStorage.getItem(RECENT_USERS_KEY) || "[]");
      } catch {
        ids = [];
      }
      return (Array.isArray(ids) ? ids : [])
        .map((id) => userOptions.find((o) => o.id === id))
        .filter((o): o is (typeof userOptions)[number] => Boolean(o));
    },
    [RECENT_USERS_KEY, userOptions],
  );
  const rememberUserPick = (id: number) => {
    try {
      const prev: unknown[] = JSON.parse(localStorage.getItem(RECENT_USERS_KEY) || "[]");
      // 151D §1(b) — ten, the number the open menu leads with.
      const next = [id, ...(Array.isArray(prev) ? prev : []).filter((x) => x !== id)].slice(0, 10);
      localStorage.setItem(RECENT_USERS_KEY, JSON.stringify(next));
    } catch {
      /* a full or blocked storage loses the shortcut, never the filter */
    }
  };
  const byUser = useMemo(() => {
    if (userF == null) return typed;
    return typed.filter((e: TeRow) => e.coId === userF);
  }, [typed, userF]);

  // ---- 150 §3 — THE CASE FILTER, WHERE THE POPULATION SPANS MATTERS ----
  //
  // A row names its matter as `caId` (alias `caseId`) or `ccId` — `_teMatter`
  // at the top of this file reads the same spellings for provenance, and the
  // 125 §2 rule (read every spelling, write one) governs here too.
  const byCase = useMemo(() => {
    if (!showCaseFilter || !caseF) return byUser;
    return byUser.filter((e: ProvenanceRow) =>
      caseF.type === "cc" ? e.ccId === caseF.id : (e.caId ?? e.caseId) === caseF.id,
    );
  }, [byUser, caseF, showCaseFilter]);

  // ---- 150 §3 — THE PERIOD FILTER, LAST NARROWING BEFORE THE SORT ----
  //
  // `entries → tab → search → activity → user → case → period → sort`. The bounds
  // come from lib/periodRange — ONE copy of the date arithmetic, and the work
  // week is the FIRM'S (scheduleAxis; the Mon–Fri constants only through its
  // fallback). Comparison is on the date-string prefix, the way every window
  // in this tree compares (`e.date >= from && e.date <= to` — Dashboard's own
  // idiom); a row with no date has no answer to "which period" and leaves when
  // one is chosen, exactly as the sort files no-answer rows last.
  const byPeriod = useMemo(() => {
    const b = periodBounds(period, schedAxis);
    if (!b) return byCase;
    const [from, to] = b;
    return byCase.filter((e: TeRow) => {
      const d = e.date ? String(e.date).slice(0, 10) : "";
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [byCase, period, schedAxis]);
  // The filtered, pre-tab population — what the seven counters read.
  const filtered = byPeriod;
  const tabbed = useMemo(() => filtered.filter(tabFilter), [filtered, effTab, justMarked]);
  // 213 addendum 3 §18 [RULED 38] — the rows on screen are `tabbed`: every filter,
  // then the tab (`visible` below only sorts them, the selection only ticks them).
  // Counted and their minutes summed, for the counter beside the tab strip (213B §1).
  const shown = useMemo(() => shownOf(tabbed), [tabbed]);
  // 213B §2 [RULED 39] — the sum of what the Amount column shows for them: the
  // strip's own reduction over the same rows (never a second computation of
  // money); null until the served money arrived, which the counter reads « — ».
  const shownAmt = useMemo(() => shownMoney(tabbed, feeCalc), [tabbed, feeCalc]);

  /* ── 150 §4 → 190 ROUND 5 [O] → ROUND 6 [W] — THE SEVEN COUNTERS ────────
     One model with the Récapitulatif (round 5): NON MARQUÉ · NON FACTURABLE ·
     FACTURABLE · FACTURÉ ┃ ENVOYÉ · PAYÉ · DÛ — left of the line the WORK
     over this host's rows, incl. costs and VAT (the served per-row `ttc`,
     summed here); right of it the MATTER'S LIVE DOCUMENTS AS ISSUED (the
     served `recap`, read). The strip itself is CountersStrip.tsx, ONE
     component for three hosts; this file declares TWO of them:
       matter     kind case / cc — `filtered` is the reader's filter, the
                  matter is `cs`.
       dashboard  kind "all" — the population spans matters: the four left
                  over the filtered rows, the three right summed over the
                  matters those rows sit on ("his matters, narrowed by the
                  dashboard's own filters, all seven").
     The population is the host's declaration, the arithmetic is the one
     `stripFigures`. */
  const stripHost: StripHost = kind === "all" ? "dashboard" : "matter";
  const figures = useMemo<StripFigures | null>(() => {
    if (!canSeeCounters) return null;
    let matters: unknown[] = [];
    if (spansMatters) matters = [...new Set((filtered as unknown[]).map((e) => caseOf(e) as unknown).filter(Boolean))];
    else if (cs) matters = [cs as unknown];
    return stripFigures(filtered as Parameters<typeof stripFigures>[0], feeCalc, matters);
  }, [filtered, feeCalc, cs, canSeeCounters, spansMatters]);

  // ---- 146 §5 — SORTABLE COLUMN HEADERS ----
  //
  // New behaviour, not a repair: the headers were plain `<th>` cells with no
  // click handler and no sort state anywhere in the file.
  //
  // AND THE SORT APPLIES TO THE SEARCH RESULT — the whole point, said twice in
  // the brief. `entries → tab → search → sort`, and the SORTED set is what
  // `visible` becomes, so §4's select-all guarantee survives untouched:
  // `selectableVisible`, `toggleAll` and `allVisibleSelected` all read `visible`
  // and therefore see exactly the rows on screen, in the order they are on
  // screen. Any other order and the two features fight.
  //
  // TWO THINGS THE BRIEF LEAVES TO ME, decided here, both by doing nothing:
  //
  //   · A SORT SURVIVES A TAB CHANGE. `onTabChange` does not touch `sort`. A
  //     sort is how you are reading the list, not which slice of it you are
  //     reading; re-reading it in an order you did not choose would be the
  //     surprise. The search box already behaves this way, and clearing one but
  //     not the other would be the real inconsistency.
  //   · A SORT DOES NOT SURVIVE A RELOAD. It is `useState`, so it dies with the
  //     mount — exactly like the tab and the search box. Persisting it needs
  //     machinery §5 forbids, and brief 147 restructures this block, so a
  //     storage key minted today is one 147 inherits or retires.
  //
  // And the DEFAULT is no sort at all: until a header is clicked the list reads
  // exactly as it does today, in host order. Opening every user's list in a new
  // order is a change nobody ruled on, and §3 already changes what they see
  // first.
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const toggleSort = (key: SortKey) =>
    setSort((p) => (p && p.key === key ? { key, dir: p.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  // One value per sortable column, and each reads WHAT THE CELL RENDERS rather
  // than what the row happens to carry: QUI sorts on the collaborator's printed
  // name, not on `coId`, and MONTANT is null exactly where the cell prints "—"
  // (`ef.er > 0` is the cell's own condition) — sorting by a number the user
  // cannot see is the surprise this avoids. TAUX and MONTANT come from
  // `feeCalc`, which is server-handed and asynchronous, so before the first
  // response every row's value is null and the sort is a no-op; it lands when
  // the money does.
  const SORT_VAL: Record<SortKey, (e: TeRow) => number | string | null> = {
    date: (e) => {
      const ms = e.date ? new Date(e.date as string).getTime() : NaN;
      return Number.isFinite(ms) ? ms : null;
    },
    who: (e) => {
      const c = co.find((x: { id?: number }) => x.id === e.coId);
      return c ? pname(c) : null;
    },
    type: (e) => e.type || null,
    min: (e) => (typeof e.min === "number" ? e.min : null),
    rate: (e) => {
      const ef = feeCalc ? feeCalc[String(e.id)] : null;
      return ef && typeof ef.er === "number" ? ef.er : null;
    },
    amt: (e) => {
      // [AU] — sorts by the figure the cell shows (fees + costs)
      const ef = feeCalc ? feeCalc[String(e.id)] : null;
      return ef && ef.er > 0 && typeof ef.wc === "number" ? ef.wc : null;
    },
  };

  const visible = useMemo(() => {
    if (!sort) return tabbed;
    const val = SORT_VAL[sort.key];
    // A COPY — a memo may never sort another memo's array in place.
    return [...tabbed].sort((a: TeRow, b: TeRow) => {
      const av = val(a);
      const bv = val(b);
      // A row with no answer to the question asked sorts LAST in BOTH
      // directions — a column of dashes leading a descending sort is noise
      // where the top of the list is the part being read.
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      const c = typeof av === "string" || typeof bv === "string"
        ? String(av).localeCompare(String(bv))
        : av - bv;
      return c * sort.dir;
    });
    // `Array.prototype.sort` is stable (ES2019), so rows that tie keep the
    // search order, which is the host's order. That is what makes clicking TYPE
    // on a list already sorted by date read the way it looks like it should.
  }, [tabbed, sort, co, feeCalc]);

  // The header cell, as a lowercase helper INVOKED AS A CALL — `typeCell` and
  // `pill` below are the same shape. A PascalCase component declared in here and
  // mounted as JSX is precisely what `scripts/lint-inline-components.js` fails
  // the build on (Bug-A), and it would remount this thead on every render.
  const sortTh = (key: SortKey, label: string) => {
    const dir = sort && sort.key === key ? sort.dir : 0;
    return (
      <th className="teSortTh" onClick={() => toggleSort(key)} style={dir ? { color: "var(--ac)" } : undefined}>
        {label}
        {dir === 1 && <ChevronUp size={11} style={SORT_ARROW} />}
        {dir === -1 && <ChevronDown size={11} style={SORT_ARROW} />}
      </th>
    );
  };

  const tabOptions = [
    ["all", t("te.tab.all", { fallback: "All" })],
    ["unmarked", t("te.tab.unmarked", { fallback: "Unmarked" })],
    ["billable", t("te.tab.billable", { fallback: "Billable" })],
    ["unbillable", t("te.tab.unbillable", { fallback: "Unbillable" })],
  ];
  if (showInvoicedTab) tabOptions.push(["invoiced", t("te.tab.invoiced", { fallback: "Invoiced" })]);

  // ---- selection ----
  const selEntries = useMemo(() => entries.filter((e: any) => sel.has(e.id)), [entries, sel]);
  const selMinutes = selEntries.reduce((a: any, e: any) => a + (e.min || 0), 0);
  // Phase 35 (C1) — the Invoice button pulls ONLY billable, not-yet-invoiced
  // selected entries into a pro forma (billable is the only state a pro forma
  // draws from; the server re-affirms invoiceId + mark on create).
  const billableSel = useMemo(
    () => selEntries.filter((e: any) => !e.invoiceId && markOf(e) === TE_MARK.BILLABLE),
    [selEntries],
  );
  const billableSelMin = billableSel.reduce((a: any, e: any) => a + (e.min || 0), 0);
  const toggleRow = (id: any, on: any) => setSel((p) => { const n = new Set(p); on ? n.add(id) : n.delete(id); return n; });
  // Phase 35 Stage-3 r2 (§7) — invoiced rows are never selectable (select-all and
  // bulk mark skip them; the row checkbox is disabled below).
  const selectableVisible = useMemo(() => visible.filter((e: any) => !e.invoiceId), [visible]);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((e: any) => sel.has(e.id));
  const toggleAll = (on: any) => setSel(() => (on ? new Set(selectableVisible.map((e: any) => e.id)) : new Set()));

  // ---- 146 §1 — THE SELECTION MAY ONLY EVER POINT AT ROWS THAT ARE HERE ----
  //
  // RULED by Laurent 2026-08-29, after seeing it on staging. 145 §1.4 made the
  // selection SURVIVE a bulk edit, correctly. But an entry the edit MOVES to
  // another matter leaves this table while its id stays in `sel`. The bar then
  // reads `(3 · 0m)` — three ids, no minutes, because `selMinutes` sums
  // `selEntries`, which is built from `entries` and finds none of them — with
  // nothing ticked on screen AND ITS BUTTONS STILL LIVE. `bulkDelete` posts
  // `[...sel]` verbatim, so *Delete selected* there hard-deletes three rows in a
  // matter the user is not looking at, and the server's per-row ladder passes
  // because they are RP of both. THE ARMED BUTTONS ARE THE DEFECT; the counter
  // was only the tell.
  //
  // WHERE THE DEPARTURE HAPPENS, ESTABLISHED RATHER THAN ASSUMED — THE PARENT
  // RE-FILTERS, AND THIS TABLE IS TOLD NOTHING AT ALL.
  // `CaseTimeEntriesSection.tsx:141` filters App's global `te` down to this
  // matter (`e.caId === cs.id` / `e.ccId === cs.id`); `bulkEditDone`'s `apply()`
  // writes the moved rows back into `te` carrying their NEW matter, so the next
  // render simply hands this component a shorter `entries` array. There is no
  // removal event to hook. And `bulkEditDone` could not compute the answer
  // itself either: it would have to re-derive the host's own scoping predicate,
  // which differs per `kind` — the invoice surface's entries span matters — and
  // that is rule 4's "never approximate" exactly.
  //
  // SO THE DROP IS A RECONCILIATION AGAINST `entries`, which is the one place it
  // cannot miss: it covers the bulk edit that prompted the ruling, and equally a
  // single-row edit that moves a matter, an SSE push from another user, and a
  // change to the host's own billed-status filter.
  //
  // ONLY THE DEPARTED — 145 §1.4's ruling stands. Rows that stayed, including
  // the ones a bulk edit SKIPPED on a provenance collision, keep their ticks.
  // And it reconciles against `entries` and NOT `visible`: a row moved to
  // another TAB by a mark change is still in this matter, still one click away
  // and still a row the user can see, so dropping it would undo 145 §1.4
  // through the back door.
  //
  // useLayoutEffect, not useEffect: a deferred effect would let the bar PAINT
  // once, armed, pointing at rows that are gone. One frame is enough to click
  // in — this defect was found by nearly clicking it — so the reconciliation
  // runs before the paint that would show it.
  //
  // Keyed on the ids' own VALUE, not on `entries`: hosts pass a fresh array
  // identity every render (see `_efKey` above), so `[entries]` would run this on
  // every render of every host. The setter is the functional form and returns
  // `prev` unchanged when nothing left, so a render that drops nothing schedules
  // nothing — and `sel` is deliberately not a dependency (an effect that lists
  // the state it sets is `effect-sets-its-own-dep`'s whole subject).
  const presentKey = entries.map((e: { id: number }) => e.id).join(",");
  useLayoutEffect(() => {
    setSel((prev: Set<unknown>) => {
      if (!prev.size) return prev;
      const present = new Set(entries.map((e: { id: number }) => e.id));
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [presentKey]);

  // ---- mark write (the model, once) — and 146 §2, ITS UNDO ----
  //
  // *Mark as billable* / *Mark as unbillable* / *Unmark* change what will be
  // BILLED, and they were the only bulk acts in this bar with no way back: bulk
  // delete got the 30-second window in 144 §5 and bulk edit in 145 §1, marking
  // got neither, and it is the button in this bar people press most.
  //
  // 145's BEFORE-VALUES PATH DOES NOT GENERALISE HERE, AND THE REASON IS THE
  // GATE, NOT THE SHAPE. `PUT /bulk` + `POST /bulk-revert` are the same move
  // with a smaller payload and would have been the reuse §2 asks for — but both
  // re-judge every row on `caseView.timeEntries.bulkEdit` AND
  // admin-or-RP-of-the-matter (`timeEntries.js:486` and `:590`, through
  // `makeMatterGate`). These three buttons render for EVERY user on their own
  // rows — this file's own header says so, and `shapes.js` is what limits a
  // non-RP's `te` to own rows — so routing marks through the bulk endpoint
  // would start refusing an ordinary collaborator marking their own entries.
  // Rule 4: widen or leave alone, never bend the behaviour to fit.
  //
  // AND NO THIRD MECHANISM IS BUILT. This is the SHARED HOOK in the shape its
  // own header prescribes — "the hook is dumb, it does not know how to snapshot
  // or restore anything" — with the snapshot and the restore as closures here.
  // The write goes through `s()` → `PUT /time-entries/:id`, per row, on BOTH
  // legs, so the undo carries exactly the authority the forward write carried
  // and there is nothing for a signature to prove. (The bulk revert needs one
  // because there the SERVER hands back values the client never held; here the
  // client puts back a value it overwrote a moment ago.)
  //
  // NO CONFIRM DIALOG — `label: null`, which is 145 §1's own confirm-free arm.
  // A mark is one click, non-destructive, and now reversible; a modal in front
  // of the three most-used controls in this bar would be a regression §2 did
  // not ask for. The undo IS the safety net, which is what it did ask for.
  //
  // A 15s window (TE_MARK_UNDO_MS below — 151D §3(b) halved the bulks' 30s
  // for marks), and the same single-slot cost: for those seconds any other
  // undoable act commits this one early, and this one replaces any pending
  // window when it opens.
  const teRef = useRefLatest(te);
  const applyMark = (mark: string) => {
    if (!sel.size || !s) return;
    // The rows this will ACTUALLY touch, with their marks as they stand. §7's
    // invoiced refusal picks the set, so a row the forward write skips is a row
    // the undo never puts back either.
    const before: MarkSnap[] = (te || [])
      .filter((e: TeRow) => sel.has(e.id) && !e.invoiceId)
      .map((e: TeRow) => ({ id: e.id, mark: markOf(e) }));
    const msg = t("te.toast.marked_" + mark, {
      fallback: mark === "billable" ? "Marked billable" : mark === "unbillable" ? "Marked unbillable" : "Unmarked",
    });
    // ONE writer for both legs, and it reads `teRef`, never the `te` prop. The
    // restore closure is built at click time and fires up to 30 seconds later,
    // and `persist` DIFFS the array it is handed against live state
    // (`App.tsx:4684`) — so a stale `te` would not merely miss an update, it
    // would DELETE every row created in the meantime. This is the precise
    // hazard `useRefLatest`'s own header was written for.
    const write = (rows: MarkSnap[]) => {
      const by = new Map(rows.map((r): [unknown, string] => [r.id, r.mark]));
      s(SK.te, (teRef.current || []).map((e: TeRow) => {
        const m = by.get(e.id);
        // §7 — never re-mark an invoiced entry (kills the false "remarked"
        // toast). Re-checked on the way back too: a row invoiced INSIDE the
        // window is not re-marked by an undo, for the same reason
        // `/bulk-revert` re-applies its own invoiced refusal.
        return m !== undefined && !e.invoiceId ? { ...e, mark: m } : e;
      }), sTe);
    };
    /* 147 §1.4(a) — REPAIR OF 146 §2, AND THE FILE DISAGREED WITH ITSELF.
       `setSel(new Set())` stood here and is GONE. 146's other half was written
       to PRESERVE this selection: the reconciliation at :526 drops only ids
       absent from `entries`, and its own comment (:509) says why — a row "moved
       to another TAB by a mark change is still in this matter, still one click
       away and still a row the user can see, so dropping it would undo 145
       §1.4 through the back door". Then this line dropped the whole selection
       three hundred lines later. RULED: it goes. 145 §1.4's rule — a selection
       survives a bulk act — governs the mark path too, and without it the undo
       toast offered a reversal of something the screen no longer showed: no
       rows, no ticks.
       Delete still clears, in its own onConfirm (:759): a deleted row cannot
       stay ticked. That distinction is the one 145 §1.4 drew. */
    const commit = () => {
      write(before.map((r) => ({ id: r.id, mark })));
    };
    // Nothing to put back — every selected row was invoiced under us — so no
    // window opens, the same shape as `bulkEditDone`'s missing revert token.
    // No `justMarked` either: there is no window for it to follow.
    if (!confirmAndUndo || !before.length) {
      commit();
      toast && toast(msg);
      return;
    }
    /* IDENTITY-GUARDED ON BOTH EDGES, and that is not defensive coding. The
       hook fires the PREVIOUS window's `onExpire` from inside `runDelete`,
       AFTER this call's `onConfirm` has already run (useConfirmAndUndo.tsx:97
       then :102) — so a second mark inside the first one's 30 seconds would
       have the old window's edge wipe the new window's set. `p === held` is
       what makes each edge clear only its own. */
    const held = { tab, ids: new Set(before.map((r) => r.id)) };
    const release = () => setJustMarked((p) => (p === held ? null : p));
    confirmAndUndo({
      label: null,
      toastName: msg,
      windowMs: TE_MARK_UNDO_MS, // 151D §3(b) — the mark toast halved; bulks keep 30s
      onConfirm: () => { commit(); setJustMarked(held); },
      /* §4 — onExpire IS the commit edge (expiry, displacement, unmount):
         the rows leave `held.tab` here and arrive under `mark`'s own tab,
         which pulses. The Undo arm below releases WITHOUT pulsing — restored
         rows never left the eye. A destination the strip cannot show (the
         viewer lost the mark key mid-window) pulses nothing: pulseKey
         matches no rendered tab and the class lands nowhere. */
      onExpire: () => { release(); firePulse(mark); },
      undo: { snapshot: before, restore: (snap: MarkSnap[]) => { write(snap); release(); } },
    });
  };
  const dbl = (n: any) => `${n} · ${fmtHM(selMinutes)}`;
  /* 213B §20 [RULED 48] — the bulk row's buttons stand there with nothing
     ticked, dimmed and inert, their labels bare; a tick lights them and the
     count returns. One reading of the selection for every button. */
  const noSel = sel.size === 0;
  const noBill = billableSel.length === 0;
  /* The count sits in a box of FIXED width (12ch, tabular digits), and the
     box is kept while a label is bare — hidden, not removed (149 §3's own
     way: hidden-not-unmounted). The labels' width decides whether this row
     wraps, and a wrap on a tick moves the table: the drive measured the row at
     19 px bare and 44 px with its counts on a case at 1440 px in French, and a
     hidden placeholder a few pixels narrower than « (1 · 1h 30m) » still
     flipped it. A hidden span is read by nobody (innerText and the
     accessibility tree both skip it); the dimmed label shows no count. */
  const countBox = (shown: boolean, children: React.ReactNode) => (
    <span aria-hidden={!shown} style={{ display: "inline-block", minWidth: "12ch", fontVariantNumeric: "tabular-nums", visibility: shown ? "visible" : "hidden" }}>
      {children}
    </span>
  );
  const selCount = noSel ? countBox(false, ` (${dbl(0)})`) : countBox(true, ` (${dbl(sel.size)})`);
  const bulkDim = (off: boolean) => (off ? { opacity: 0.45, cursor: "default" } : {});

  // ---- 144 §5 — bulk delete, and the long undo ----
  // 30s, and the cost is stated (the brief demands it): the hook is
  // SINGLE-SLOT, so for those 30 seconds ANY other undoable act — deleting a
  // document, a stage, a single entry — commits this window early, and this
  // window replaces any pending one when it opens. A longer window buys more
  // regret-time and hogs the slot longer; 30s is taken as the brief's own
  // default because no measurement distinguishes the candidates — it is not a
  // real choice, and saying so is the requirement.
  //
  // The snapshot is a BOX the delete response fills: the hook captures
  // snapshots synchronously at click time, but the restore payload — the
  // SIGNED verbatim rows — exists only once the server answers, so Undo
  // awaits the box's promise. A failed delete resolves the box null and Undo
  // becomes a no-op (nothing was deleted; the rows never left local state).
  const TE_BULK_UNDO_MS = 30_000;
  /* 151D §3(b) — the MARK-undo toast is HALVED: 30_000 → 15_000, for
     applyMark alone. The toast's progress bar and the undo window are the
     same clock (useConfirmAndUndo), so halving the toast is halving the
     window. Bulk delete and bulk edit keep the 30s above — the brief names
     the mark toast, and a destructive act earns the longer regret-time. */
  const TE_MARK_UNDO_MS = 15_000;
  const BULK_SKIP_KEY: Record<string, [string, string]> = {
    "timeEntry.invoiced_locked": ["te.bulk.skip.invoiced", "already invoiced"],
    "generic.forbidden": ["te.bulk.skip.forbidden", "no authority"],
    "timeEntry.provenance_collision": ["te.bulk.skip.collision", "same source message already logged there"],
    "generic.not_found": ["te.bulk.skip.notfound", "no longer exists"],
    "generic.server_error": ["te.bulk.skip.error", "failed"],
  };
  const bulkSkipSummary = (skipped: { id: number; code: string }[], head: string) => {
    const nameOf = (id: number) => {
      const row = (te || []).find((x: { id?: number }) => x.id === id);
      if (!row) return `#${id}`;
      const when = row.date ? fd(row.date) : "";
      const what = typeof row.desc === "string" && row.desc ? row.desc : typeof row.type === "string" ? row.type : "";
      return [when, what].filter(Boolean).join(" ") || `#${id}`;
    };
    const byCode = new Map<string, number[]>();
    for (const s0 of skipped) {
      const a = byCode.get(s0.code) || [];
      a.push(s0.id);
      byCode.set(s0.code, a);
    }
    const parts = [head];
    for (const [code, list] of byCode) {
      const [k, fb] = BULK_SKIP_KEY[code] || BULK_SKIP_KEY["generic.server_error"];
      const names = list.slice(0, 4).map(nameOf).join(", ") + (list.length > 4 ? ` +${list.length - 4}` : "");
      parts.push(`${t(k, { fallback: fb })}: ${names}`);
    }
    return parts.join(" · ");
  };
  // ---- 145 §1 — the bulk EDIT's undo, on the SAME slot as the delete's ----
  //
  // 144 §5 gave the long undo to delete alone because §5 named only delete;
  // what Laurent asked for was the undo on the MODIFICATION. This is the same
  // move with a smaller payload — the server's signed BEFORE values instead of
  // whole rows — and it deliberately lands HERE rather than in the modal that
  // performs the edit.
  //
  // WHY HERE. `confirmAndUndo` is SINGLE-SLOT PER HOST, and the host of this
  // table (CaseView / CollectiveCaseView / InvoiceView) is the one whose slot
  // the bulk DELETE already takes. The bulk modal renders from App's `rMod`,
  // which owns a different instance: putting the edit's toast there would give
  // two adjacent buttons in one bar two undo windows that cannot close each
  // other, and both toasts are `position: fixed; bottom: 16; left: 50%` — they
  // would land on top of one another. So the modal owns the FIELDS and hands
  // the result back through the `_onDone` it was opened with; this owns the
  // RESULT: the local patch, the summary, and the window.
  //
  // `label: null` skips the confirm dialog (the hook's own shape for a
  // confirm-free undo) because the modal WAS the confirmation. `onConfirm`
  // applies the edit to local state rather than performing it — the write
  // already happened server-side, and the hook only requires that this is the
  // point of no return.
  //
  // NO REVERT TOKEN, NO WINDOW. The server omits `revert` when nothing actually
  // changed, so a bulk whose every row was skipped — or whose patch was a no-op
  // — gets the summary and no undo, instead of a button that would write
  // nothing.
  const bulkEditDone = (r: TeBulkUpdateResult) => {
    const byId = new Map(r.updated.map((u) => [Number(u.id), u]));
    const apply = () => sTe((prev: { id: number }[]) => prev.map((x) => byId.get(x.id) || x));
    const head = t("te.bulk.updated_n", { count: r.updated.length, fallback: `Updated ${r.updated.length}` });
    const msg = r.skipped.length ? bulkSkipSummary(r.skipped, head) : head;
    const rev = r.revert;
    if (!confirmAndUndo || !rev || !rev.rows.length) {
      apply();
      // 166 §G.3 — `msg` carries the skip summary when rows were refused; its
      // two siblings (the revert at :1329 and the re-apply at :1372) already
      // coloured that case and this one did not.
      toast && toast(msg, r.skipped.length ? "error" : undefined);
      return;
    }
    confirmAndUndo({
      label: null,
      toastName: msg,
      windowMs: TE_BULK_UNDO_MS,
      onConfirm: apply,
      undo: {
        snapshot: rev,
        restore: (snap: TeBulkRevert) => {
          void (async () => {
            try {
              const rr = await bulkRevertTimeEntries(snap);
              if (rr.reverted.length) {
                const back = new Map(rr.reverted.map((u) => [Number(u.id), u]));
                sTe((prev: { id: number }[]) => prev.map((x) => back.get(x.id) || x));
              }
              const h = t("te.bulk.reverted_n", { count: rr.reverted.length, fallback: `Reverted ${rr.reverted.length}` });
              toast && toast(rr.skipped.length ? bulkSkipSummary(rr.skipped, h) : h, rr.skipped.length ? "error" : undefined);
            } catch (e) {
              toast && toast((e as Error).message, "error");
            }
          })();
        },
      },
    });
  };
  const bulkDelete = () => {
    if (!confirmAndUndo || !sel.size) return;
    const ids = [...sel] as number[];
    const box: { promise: Promise<TeBulkDeleteResult | null> | null } = { promise: null };
    confirmAndUndo({
      label: t("te.bulk.delete_label", { count: ids.length, fallback: `${ids.length} time entries` }),
      toastName: t("te.bulk.deleted_n", { count: ids.length, fallback: `Deleted ${ids.length}` }),
      windowMs: TE_BULK_UNDO_MS,
      onConfirm: () => {
        setSel(new Set());
        box.promise = bulkDeleteTimeEntries(ids)
          .then((r) => {
            const gone = new Set(r.deleted);
            sTe((prev: { id: number }[]) => prev.filter((x) => !gone.has(x.id)));
            if (r.skipped.length) {
              // 166 §G.3 — guarded on `skipped.length`, so this toast is ALWAYS
              // a partial refusal, and it was always green.
              toast && toast(bulkSkipSummary(r.skipped, t("te.bulk.deleted_n", { count: r.deleted.length, fallback: `Deleted ${r.deleted.length}` })), "error");
            }
            return r;
          })
          .catch((e: Error) => {
            toast && toast(e.message, "error");
            return null;
          });
      },
      undo: {
        snapshot: box,
        restore: (snap: { promise: Promise<TeBulkDeleteResult | null> | null }) => {
          void (async () => {
            const r = snap.promise ? await snap.promise : null;
            if (!r || !r.deleted.length) return; // delete failed or deleted nothing — nothing to put back
            try {
              const rr = await bulkRestoreTimeEntries(r.restore);
              if (rr.restored.length) sTe((prev: { id: number }[]) => [...prev, ...rr.restored]);
              const head = t("te.bulk.restored_n", { count: rr.restored.length, fallback: `Restored ${rr.restored.length}` });
              toast && toast(rr.skipped.length ? bulkSkipSummary(rr.skipped, head) : head, rr.skipped.length ? "error" : undefined);
            } catch (e) {
              toast && toast((e as Error).message, "error");
            }
          })();
        },
      },
    });
  };

  // ---- columns (descriptor-driven → thead/tbody always aligned) ----
  // Brief 86 §2c — the RATE column reaches the collective case too, so the two
  // Time entries sections match. It was `kind === "case"` alone while its twin
  // on the next line already read both, which is the one-sided-correction shape
  // RULINGS.md's 2026-08-16 "DRIFT, NOT DESIGN" entry describes: nothing about
  // a collective case makes a rate less applicable, and CCV was already
  // rendering the AMOUNT that rate produces.
  //
  // Nothing else is owed. `cOf` is the collective case for kind "cc" and
  // carries both fields the cell reads — `referenceHourlyRate`
  // (schema.prisma:190, on CollectiveCase) and `caseCollaborators` — and `ef.er`
  // is already in hand, since ef.amt is what showAmt has been rendering there.
  const showRate = (kind === "case" || kind === "cc") && canTerms;
  const showAmt = (kind === "case" || kind === "cc") && canTerms;
  // 150 §1 — on 'all' the Amount column follows the host's money hint
  // (Dashboard gates it on the viewer's own-time-financial right, as its
  // hand-rolled card did); 'invoice' keeps its unconditional column — that
  // surface renders only for viewers its host already gates.
  const showAmount = kind === "invoice" || (kind === "all" && canTerms);
  // 213B §2 [RULED 39] — where the column shows, the counter beside the tabs ends
  // with the sum of what it shows; elsewhere at the hours. (Both read through
  // Boolean: `canTerms` is an untyped prop, and the file's ESLint count may not rise.)
  const showMoney = Boolean(showAmt) || Boolean(showAmount);
  const whoHeader = spansMatters
    ? t("te.col.collaborator", { fallback: "Collaborator" })
    : t("te.col.who", { fallback: "Who" });

  /* 147 §1.1 — THE SEARCH BOX SHOWS A MAGNIFIER AND NO WORDS, and the string
     it used to paint becomes its accessible NAME instead.

     146 §4 put the field's name inside the box on the argument that "a box
     labelled only Search… invites a user to type a colleague's name". The box
     is 170px wide (`App.css:373`) and the French string is 34 characters, so
     what actually rendered was *"Rechercher dans les descr"* — a sentence cut
     mid-word, which states the scope less clearly than saying nothing and
     costs the clutter as well. RULED: drop the visible text; the magnifier is
     the affordance.

     A PLACEHOLDER IS NOT A LABEL. It is the input's own content, announced
     only while the field is empty, and an input with nothing else is an
     unnamed text box to a screen reader. So the same seeded string becomes
     `aria-label` — the accessible name proper — and `title`, which is the
     hover a sighted user gets in place of the truncated line. No key is
     minted: `te.search.desc_placeholder` already exists (146's, seeded in
     `server/scripts/data/translations_seed.json`), and its three languages
     already say exactly what this field is. */
  const searchName = t("te.search.desc_placeholder", { fallback: "Search descriptions…" });

  const typeCell = (e: any) => {
    const tk = tkTypes.find((x: any) => x.code === e.type);
    /* ── 166 §B — AN IMPORTED CODE THE TAXONOMY DOES NOT KNOW WEARS THE
       STATED COLOUR ─────────────────────────────────────────────────────────

       Laurent's ruling, 2026-09-04: *a value the app did not produce itself
       renders in the STATED colour* — a stated retainer due (164 §A.7,
       `CollectiveCaseView.tsx:8272`) and, now, an activity code that names no
       taxonomy entry. Same token, same sentence: "this came from a file, not
       from the app".

       WHERE THE VALUE COMES FROM, measured: `TimeExcel.tsx:324` looks the
       sheet's code up in `tkTypes` and `:357` writes `validType ?
       validType.code : typeCode || ""` — an unmatched code is imported AS IT
       STANDS, deliberately (20 §3's reversibility: a row keeps showing what
       it holds). Until now it rendered in the accent, identical to a code the
       app itself offers, so nothing on the screen said which was which.

       The question is already asked one line up — `tk` is the answer — so no
       second rule is written here, and the colour and the tooltip can never
       disagree.

       THE `tkTypes.length` GUARD is not decoration. The list arrives from the
       taxonomy API, so it is `[]` on the first paint; without this every chip
       in the table would flash stated blue before the vocabulary landed. It is
       `valueState`'s own first guard (`taxonomyMatch.mjs:109` returns
       'unknown' for an empty entry list) — with no vocabulary loaded, nothing
       can be said to be missing from it.

       The hint is `taxonomy.orphan.hint`, the key `TaxonomyOrphanMark.tsx:46`
       and `FieldSelect.tsx:209` already use for exactly this fact. No key is
       minted. */
    const tkOrphan = !!e.type && !tk && tkTypes.length > 0;
    return (
      <>
        {e.type ? (
          <span
            title={
              tkOrphan
                ? t("taxonomy.orphan.hint", {
                    fallback:
                      "This entry no longer exists in the list — the value shown is what the record still holds. Pick a current one to relink it.",
                  })
                : tk?.desc
            }
            style={{
              fontSize: FS.s9_5,
              padding: "1px 5px",
              borderRadius: RAD.r3,
              /* the border is on BOTH faces — transparent when the code
                 resolves — so a table holding one orphan does not sit a
                 pixel taller than one holding none */
              border: "1px solid " + (tkOrphan ? "var(--st)" : "transparent"),
              background: tkOrphan ? "transparent" : "var(--as)",
              color: tkOrphan ? "var(--st)" : "var(--ac)",
              fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 600,
              ...(tkOrphan ? { cursor: "help" } : {}),
            }}
          >
            {e.type}
          </span>
        ) : <span style={{ color: "var(--tm)" }}>—</span>}
        {/* ── BRIEF 129 §6 — THE PROVENANCE IS CLICKABLE AGAIN, AND THIS IS A
            REGRESSION REPAIR ─────────────────────────────────────────────────

            126 §9 ruled "the email is a document, not a button — the doc row is
            the only affordance", and retired 125's control from here. That is
            TRUE for a task and an event, which HAVE a documents plane, and
            FALSE for a time entry, which has one provenance FIELD and no plane.
            So on this surface the ruling removed a click instead of moving it.
            CC said so at the time — "that removes a click rather than moving
            it" — and it was relayed as a neutral fact rather than named as the
            regression it is. Naming it now: THIS IS A REGRESSION, introduced by
            126 §9, repaired here.

            Laurent: "the time entries from emails show an email icon, store the
            email ID somewhere — why couldn't they, just as tasks and events,
            show email or document icons, and store also the email or document
            path under it so one can instantly check?"

            THE EMAIL HALF IS COMPOSITION, NOT CONSTRUCTION. `e.egid` is the
            internet message id; `emailDocUrl` writes the `message:` URI and
            `openEmailDocHref` resolves it on demand — two functions 126 already
            shipped, carrying every one of 125's named refusals verbatim. No new
            message is invented here.

            THE DOCUMENT HALF IS THE NEW WORK. `docItemId` is a Graph drive item
            id with no scheme, no row and no meaning outside its matter, so it
            gets its own opener (`lib/docItemLink.ts`, a sibling — that file's
            header says why it is not `emailLink.ts`) and its own glyph, the
            same `FileText` this table already imports for its document column.

            AND THE PATH IS ALREADY SHOWN, WHICH IS WHY NOTHING IS ADDED BESIDE
            IT. Measured before writing anything, on both halves:
              · an email-derived entry's `desc` is `correspondents – subject`
                (`emailDropResolve.js:109`);
              · a document-derived entry's `desc` is the FILENAME
                (`documentBilling.js`, `${fname}${suffix}`);
            and the Description column renders `e.desc` with the full string in
            its `title` (:380 below). Both rows already state their fact, so
            printing a filename or a sender under them would be stating it
            twice (brief 35 §5c). What was genuinely MISSING is the document
            GLYPH — a time entry billed from a document showed no provenance at
            all, while an email one showed a tell — and the click on both.

            THE MATTER COMES OFF THE ROW, not off `cs`: this table also renders
            `kind="invoice"`, whose entries span matters, and the drive an item
            id addresses is resolved from the matter's own folder. */}
        {(e.egid || e.docItemId) && m365 === true && (
          <button
            type="button"
            data-te-provenance={e.egid ? "email" : "document"}
            title={
              e.egid
                ? t("cc.te.tip.logged_from_email", { fallback: "Logged from email" })
                : t("cc.te.tip.logged_from_doc", { fallback: "Logged from a document — open it" })
            }
            onClick={(ev) => {
              ev.stopPropagation();
              if (e.egid) void openEmailDocHref(emailDocUrl(String(e.egid)), t);
              else void openDocItemHref(_teMatter(e), e.docItemId, t);
            }}
            style={{
              marginLeft: 4,
              display: "inline-flex",
              verticalAlign: "middle",
              alignItems: "center",
              color: "var(--tm)",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            {e.egid ? <Mail size={11} /> : <FileText size={11} />}
          </button>
        )}
        {/* 170 §1d [RULED 2026-09-08, Laurent] — THE SECOND ICON: the archived
            PDF, BESIDE the Outlook one rather than instead of it. Laurent's
            words: "a second icon beside it that opens the archived PDF of that
            email, keeping the Outlook one."

            IT RENDERS ONLY WHERE A POINTER EXISTS — `emailArchived` is served
            by `timeEntries.js` from the EmailArchive table, one query per page,
            so this is never a dead button. An email filed before the pointer
            table shipped, or never filed at all, simply shows the Outlook icon
            alone, which is what it showed before.

            `FileDown` rather than a second `FileText`: the document-provenance
            icon above already IS `FileText`, and two identical glyphs on one
            row would say the same thing about different files. */}
        {e.egid && e.emailArchived === true && m365 === true && (
          <button
            type="button"
            data-te-provenance="email-pdf"
            title={t("cc.te.tip.open_archived_pdf", {
              fallback: "Open the archived PDF of this email",
            })}
            onClick={(ev) => {
              ev.stopPropagation();
              void openArchivedEmailPdf(_teMatter(e), String(e.egid), t);
            }}
            style={{
              marginLeft: 4,
              display: "inline-flex",
              verticalAlign: "middle",
              alignItems: "center",
              color: "var(--tm)",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <FileDown size={11} />
          </button>
        )}
      </>
    );
  };

  // 150 §4 — the counter cell; a lowercase helper invoked as a call (the
  // sortTh/typeCell/pill discipline — Bug-A). 151D §2(e) flattened it: the
  // .detail-card shell (border, radius, own plate) is gone — seven plain
  // cells on the ONE pane, straight corners, no rectangle behind them.
  /* 151D §2(a)(b) — the invoice-level note under PAYÉ/DÛ leaves the cell
     and becomes a HOVER tooltip (~0.5s delay, CSS — a native title's delay
     is the browser's, not ours). Same seeded string, no new key. */
  /* 157 §5(a) — THE TIP IS A `title`, and the reason is a clip.
     MEASURED: `.teCntTip` was `position:absolute; bottom:calc(100% - 2px)`,
     so it laid out ABOVE its cell — clear of the value, which is why the
     brief's "paints over its own value" mechanism is NOT what the geometry
     does (reported). What the geometry DOES do is put the tip outside the
     strip's own box while the strip is a sticky floor inside the
     `overflow:auto` scroller at :1854 below: on a short list the strip sits
     near that scroller's top edge and the tip is cut off by it. A hint that
     is sometimes cut in half is worse than no hint, and the brief's ruling is
     that it must "display cleanly outside the clip or not exist".
     A `title` cannot be clipped by any ancestor — the browser draws it
     outside the document — and it is the idiom this very file already uses
     for row hints (:1907 and the pills above). The .teCntCell/.teCntTip CSS
     goes with it. */
  /* 195 §A.5 — the pill is `TeMarkPill` (module scope, above); this is its mount. */
  const pill = (e: PillRow) => <TeMarkPill e={e} t={t as TFn} />;

  return (
    <div>
      {/* ── 151C §1 — THE BLOCK'S ROWS, FIXED. Row 1 is the section header
          (title · Add · lock, the host's). Row 2: the tab strip, then ruling
          39's counter a little after its last tab, then the host's
          Import/Export (toolbarExtra), unlocked only; the row ALWAYS renders,
          so a viewer with no strip sees the counter alone (213B §1). Row 3:
          the filter fields — leading icons UNMUTED, the "what this narrows"
          wording MUTED as placeholder inside each field, and the two pickers
          open on the COMPLETE list. Row 4: the bulk bar, reserved height, as
          it stands. Row 5 (Invoicing): pro forma + dedups, below. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        {/* 150 §2 (b) — the mark strip follows the mark key; without it the
            list shows ALL (effTab above) and no strip pretends otherwise. */}
        {canMark && <SegmentedControl value={effTab} options={tabOptions} onChange={onTabChange} counts={counts} pulseKey={pulseTab} />}
        {/* 213 addendum 3 §18 [RULED 38] → 213B §1–§2 [RULED 39] — BESIDE THE
            TABS, a little after the last one (the distance Import/Export keep
            from the strip, 151D §1(a)): the rows on screen, their total
            duration with its unit, and the sum of what the Amount column shows
            for them — « 367 prestations - 36:55 h. - 4 521,30 € ». Every filter
            and the tab move it; the sort and the selection do not (`tabbed`,
            above). The amount only where the column shows (`showAmt ||
            showAmount`, below), « — » until the served money arrived, spelled
            by `fc` like the column; it stays outside the sentence (« - » and a
            figure read the same in every language) [ADVISOR]. Plain text in the
            row's muted small text, not a control [ADVISOR]; shown locked and
            unlocked; inside the host's ListNotLoaded, so a list that did not
            load never reads « 0 - 00:00 ». Without a strip it is the row's first
            item and keeps no margin (my judgement: the distance is from the
            strip, not from the row's edge). */}
        <span data-te-shown="" style={{ marginLeft: canMark ? 10 : 0, color: "var(--tm)", fontSize: FS.s10, whiteSpace: "nowrap" }}>
          {(t as TFn)("te.filter.shown", withCount(shown.n, "n", {
            hhmm: fmtHHMM(shown.min),
            fallback: shown.n === 1 ? "{n} activity - {hhmm} h." : "{n} activities - {hhmm} h.",
          }))}
          {showMoney && ` - ${shownAmt == null ? "—" : fc(shownAmt)}`}
        </span>
        {/* 151D §1(a) put Import/Export NEXT TO the strip, the push gone;
            213B §1 made them follow the counter. 213B §20 [RULED 48] — they
            stand at the RIGHT END of this row, under the section's lock (row
            1's, the host's): the push is back, and the counter keeps ruling
            39's place beside the strip. `data-te-tools` is the drive's mark. */}
        {toolbarExtra && (
          <div data-te-tools="" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {toolbarExtra}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        {/* ── 158 §7 CORRECTED — THE FILTERS FOLLOW THE COLUMNS ──────────────
            "The row" in the ruling was never a table row: it is THIS one, and
            its controls must read in the order the columns below them do —
            DATE · QUI · TYPE · DESCRIPTION. It used to read exactly backwards
            (description · type · collaborator · period), so the eye crossed
            the row to find the filter for the column it was looking at.

            A REORDER, NOT A REDESIGN: the five blocks below are moved WHOLE,
            comments and all. Nothing inside any of them changed — no style,
            no width, no handler, no key.

            TWO PLACEMENTS THE RULED FOUR DO NOT COVER, both decided by
            KEEPING A NEIGHBOUR RATHER THAN INVENTING A RULE:
              · the FREE date inputs stay immediately after the period select
                they belong to. A literal reversal would have put them BEFORE
                it, where two bare date fields lead a row and belong to
                nothing — "beside it" is what the select's own comment
                promises, and it still is.
              · the CASE filter (Dashboard only — the one surface whose rows
                span matters) has no column in the table and so no place in
                the ruled order. It stays immediately after the collaborator
                picker, which is exactly where it stood before. */}
        {/* 150 §3 — the period filter, wearing the same shell. 151F §2 — the
            native select becomes the app's own menu (MenuSelect over the 151D
            popover idiom): the OS painted the old list black under macOS dark
            mode and the app could not style it. The shell and its ChevronDown
            stay (caret=false); intrinsicWidth keeps the native widest-option
            footprint so the filter row does not resize per pick; the trigger
            wears .search-box select's own paint-nothing dress. "Free" still
            opens two date inputs beside it; every narrowing routes through
            the anchor as before. */}
        <div className="search-box teActBox">
          <MenuSelect
            ariaLabel={t("te.filter.period", { fallback: "Period" })}
            title={t("te.filter.period", { fallback: "Period" })}
            value={period.kind}
            onChange={(v) => {
              narrow(() =>
                setPeriod((p: PeriodValue) => ({
                  kind: v as PeriodValue["kind"],
                  from: p.from,
                  to: p.to,
                })),
              );
            }}
            /* 174 §3 [RULED 2026-09-08, Laurent] — THE NINE, from the one
               list, in Laurent's own order. This offered six; four of the nine
               existed nowhere in the tree. "All dates" keeps its place at the
               head: it is not a period, and it is not being removed from the
               surface that already had one. */
            options={[...PERIOD_ALL_OPTIONS, ...PERIOD_OPTIONS].map(
              (o) => [o.kind, t(o.key, { ...o.vars, fallback: o.fallback })] as [string, string], // 213B §4 — the quarters' `n`
            )}
            caret={false}
            intrinsicWidth
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              width: "100%",
              minWidth: 0,
              height: "auto",
              color: "var(--tp)",
              fontSize: "var(--fs-11)",
              fontFamily: "inherit",
            }}
          />
          <ChevronDown size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
        </div>
        {period.kind === "free" && (
          /* 213B §15 — what the component HOLDS, marked for the drive beside what the inputs show */
          <div className="search-box" style={{ width: "auto", gap: 4 }} data-te-free-from={period.from ?? ""} data-te-free-to={period.to ?? ""}>
            <FreeDateInput
              aria-label={t("te.filter.period_from", { fallback: "From" })}
              title={t("te.filter.period_from", { fallback: "From" })}
              value={period.from || ""}
              style={{ width: "auto", colorScheme: "inherit" }}
              onCommit={(v) => {
                narrow(() => setPeriod((p: PeriodValue) => ({ ...p, from: v })));
              }}
            />
            <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>→</span>
            <FreeDateInput
              aria-label={t("te.filter.period_to", { fallback: "To" })}
              title={t("te.filter.period_to", { fallback: "To" })}
              value={period.to || ""}
              style={{ width: "auto", colorScheme: "inherit" }}
              onCommit={(v) => {
                narrow(() => setPeriod((p: PeriodValue) => ({ ...p, to: v })));
              }}
            />
          </div>
        )}
        {/* 150 §3 — the user filter, in the same `.search-box` shell as its two
            neighbours (148 §4.1's argument: the shell paints the chrome, the
            control inside paints nothing). The picker is the extracted
            TypeaheadPicker — recents first, then autocomplete — and picking
            routes through `narrow()` like every other narrowing. */}
        <div className="search-box" style={{ overflow: "visible" }}>
          <User size={12} style={{ color: "var(--tp)", flexShrink: 0 }} />
          {/* 151C §1(c) — person icon unmuted + "All collaborators" as the
              muted resting placeholder; opens on recents-then-the-COMPLETE-
              roster (fullListOnOpen). */}
          <TypeaheadPicker
            t={t}
            options={userOptions}
            selectedLabel={
              userF == null ? "" : (userOptions.find((o) => o.id === userF) || { label: `#${userF}` }).label
            }
            placeholder={t("te.filter.collaborators_all", { fallback: "All collaborators" })}
            onPick={(o) => {
              rememberUserPick(o.id);
              narrow(() => setUserF(o.id));
            }}
            onClear={() => narrow(() => setUserF(null))}
            getRecents={recentUserOptions}
            fullListOnOpen
            lists={CO_LIST}
            clearLabel={userF != null ? t("te.filter.user_all", { fallback: "All users" }) : undefined}
            moreLabel={(n) =>
              t("picker.more_matters", withCount(n, "n", { fallback: "+{n} more — type to narrow" }))
            }
            inputClassName=""
            inputStyle={{ display: "block" }}
            wrapperStyle={{ flex: 1, minWidth: 0 }}
          />
        </div>
        {/* 150 §3 — the case filter, only where the population spans matters
            (see the prop note above). The control is MatterPicker — the
            tree's ONE matter type-ahead — with the same recents store the
            modals write (lx:recentCases:<viewer>, App.tsx owns the writer;
            the key is the contract and this is its second reader). */}
        {showCaseFilter && (
          <div className="search-box" style={{ overflow: "visible" }}>
            <FileText size={12} style={{ color: "var(--tp)", flexShrink: 0 }} />
            <MatterPicker
              t={t}
              cases={ca}
              collectiveCases={cc}
              selectedId={caseF ? caseF.id : null}
              selectedKind={caseF ? caseF.type : null}
              placeholder={t("te.filter.case_placeholder", { fallback: "Filter by case…" })}
              onPick={(m) => narrow(() => setCaseF({ type: m._type, id: m.id }))}
              onClear={() => narrow(() => setCaseF(null))}
              getRecentCases={(userId) => {
                try {
                  const raw = localStorage.getItem("lx:recentCases:" + userId);
                  return raw ? JSON.parse(raw) : [];
                } catch {
                  return [];
                }
              }}
              currentUser={currentUser}
              clearLabel={caseF ? t("te.filter.case_all", { fallback: "All cases" }) : undefined}
              inputClassName=""
              inputStyle={{ display: "block" }}
              wrapperStyle={{ flex: 1, minWidth: 0 }}
            />
          </div>
        )}
        {/* 147 §1.2 chose a native select while the list was choose-from-a-
            dozen; 151C §1(b) rules the OTHER half of the trade back in: the
            same autocomplete as the user filter beside it, opening on the
            COMPLETE list (actOptions — taxonomy plus row-carried codes),
            typing to narrow. The chevron stays: this opens a list on click
            and should say so. */}
        <div className="search-box" style={{ overflow: "visible" }}>
          <TypeaheadPicker
            t={t}
            options={actOptions}
            selectedLabel={act ? (actOptions.find((o) => o.key === act) || { label: act }).label : ""}
            placeholder={t("te.filter.activity_all", { fallback: "All activity types" })}
            onPick={(o) => narrow(() => setAct(o.key))}
            onClear={() => narrow(() => setAct(""))}
            clearLabel={act ? t("te.filter.activity_all", { fallback: "All activity types" }) : undefined}
            moreLabel={(n) => t("picker.more_matters", withCount(n, "n", { fallback: "+{n} more — type to narrow" }))}
            fullListOnOpen
            inputClassName=""
            /* 151D §1(g) — display:block, on a measurement: an inline input
               inside the picker's wrapper div adds baseline/descender slack
               to the wrapper's line box, and the three picker shells stood
               32.84 visual px against the description/period boxes' 28.84.
               A block child is exactly its own height; the shells equalise
               with no height written anywhere. (Same fix on all three
               picker mounts in this row.) */
            inputStyle={{ display: "block" }}
            wrapperStyle={{ flex: 1, minWidth: 0 }}
          />
          <ChevronDown size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
        </div>
        {/* 146 §4 — the description search, in the tree's own `.search-box`
            idiom (App.css:373; the same shell InvoiceView's two lists and the
            agenda header use). 151C §1(a) overrules 147 §1.1's bare box: the
            magnifier is UNMUTED (it is the affordance) and the field says
            "Description" as a muted placeholder — the shell's own
            ::placeholder colour. The seeded search name stays the accessible
            name: a placeholder is announced only while the field is empty. */}
        <div className="search-box">
          <Search size={12} style={{ color: "var(--tp)", flexShrink: 0 }} />
          <input
            aria-label={searchName}
            title={searchName}
            placeholder={t("te.col.desc", { fallback: "Description" })}
            value={q}
            /* 147 §1.5 — through the anchor, like the tabs. Read the value out
               of the event BEFORE the closure: React pools nothing in 17+, but
               `narrow` calls this back after two setState calls and reading
               `ev.target` there is a habit that breaks the day it does. */
            onChange={(ev) => { const v = ev.target.value; narrow(() => setQ(v)); }}
          />
        </div>
        {/* 213 addendum 3 §18's counter ended this row; 213B §1 [RULED 39] moved
            it beside the tab strip, row 2 above. */}
      </div>
      {/* ── 149 §3 — THE BULK BAR RESERVES ITS OWN HEIGHT ─────────────────────
          148 §4.2 measured the jump (ticking a row wrapped the toolbar 23.4 →
          50.0 CSS px and the table's top moved 26.6px) and refused the move
          into the section header on the same measurement. RULED here: the
          bar's row is ALWAYS IN FLOW — its own line below the toolbar, held by
          `visibility` rather than by mount — so the table stops moving when a
          selection appears and disappears. NO PIXEL IS WRITTEN: the reservation
          is the bar's own content height, so it scales with the text size
          because it IS the bar. `visibility: hidden` keeps layout, drops the
          contents from pointer events and the a11y tree.

          The "Create Pro Forma Invoice" button MOVES IN WITH THE FIVE, and
          that is load-bearing, not tidiness: it is selection-dependent too
          (billable rows picked), and left on the toolbar row it would go on
          wrapping that row for every billing delegate — the exact jump this
          reservation exists to remove. Its own visibility condition keeps the
          Phase-35 contract (delegate + billable selection).

          The row renders in BOTH lock states, so the table's top offset is one
          constant of the section rather than a function of lock × selection.

          213B §20 [RULED 48] — UNLOCKED, THE ROW IS VISIBLE WHATEVER THE
          SELECTION (149 §3 hid it until a tick, which hid the feature: one had
          to guess that ticking a row would make buttons appear). Every button
          the viewer may use stands there; with nothing ticked it is DIMMED and
          INERT (`disabled`, the muted opacity) and its label carries no count
          [ADVISOR — no « (0 · 0h 00m) »]; one tick lights it, and everything
          is as before. Locked, the row stays hidden as it was: the reserved
          height is unchanged, so the table does not move on a tick.
          `data-te-bulk` is the drive's mark. */}
      {(canMark || canModify) && (
      <div data-te-bulk="" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6, visibility: !locked ? "visible" : "hidden" }}>
          {/* Phase 35 Stage-3 — the "Create Pro Forma Invoice" button
              (delegate-gated) reassembles the selected billable entries and opens
              the create modal; the server owns the entry→invoice binding
              (invoiceId + mark) atomically. Lit only when a billable,
              not-yet-invoiced row is picked; dimmed otherwise (213B §20). */}
          {!proFormaRow && canDelegate && onProForma && (
            <button
              className="btn"
              disabled={noBill}
              aria-disabled={noBill}
              style={{ padding: "2px 9px", fontSize: FS.s10, background: "var(--as)", color: "var(--ac)", border: "1px solid var(--bd)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, ...bulkDim(noBill) }}
              title={t("te.bulk.invoice_tip", { fallback: "Create a pro forma invoice from the selected billable entries" })}
              onClick={() => {
                if (noBill) return;
                onProForma(billableSel.map((e: any) => e.id));
                setSel(new Set());
              }}
            >
              <FileText size={12} />
              {/* 161 §4 — a NEW KEY, not a reworded one (the seed is
                  insert-if-missing; editing `te.bulk.create_proforma`'s text
                  would ship nowhere the old rows exist). Both hosts of the old
                  key change together, which is the point of a key. */}
              {t("te.bulk.invoice_activities", { fallback: "Invoice activities" })}
              {noBill ? countBox(false, ` (0 · ${fmtHM(0)})`) : countBox(true, <> ({billableSel.length} · {fmtHM(billableSelMin)})</>)}
            </button>
          )}
          {/* Phase 34 (Stage 3 r2) — the mark buttons lived on the tabs row,
              far right, until 149 §3 gave the selection bar its own reserved
              line (above).

              ── 148 §4.2 — THE MOVE INTO THE SECTION HEADER IS REFUSED, ON THE
              MEASUREMENT THE BRIEF ASKED FOR ──────────────────────────────────
              §4.2 ruled these five buttons into the section header row (title,
              *+ Add time entry*, *Export*, *Import*, the lock) because today
              they arrive as their own line and the table jumps down as they
              come and up as they go. It also said: measure the header at Très
              grand first, and if it cannot hold them, STOP AND SAY SO rather
              than ship a header that wraps into three lines. It cannot.

              THE JUMP IS REAL AND IS NOW A NUMBER: ticking a row takes the
              toolbar from 23.4 to 50.0 CSS px and the table's top moves down
              26.6px (27.0 at Moyen). That is the defect, confirmed.

              WHAT THE HEADER CAN HOLD, measured with a selection live so the
              labels carry their real counts — and with FOUR rows ticked, the
              brief's own example ("4 · 1h 30m"), not the 140 that would have
              made every button wider and the verdict unfair:

                              header  needs   over by
                  Très grand    790  1120.4    330.4
                  Grand         939  1252.5*   313.5
                  Moyen        1030  1137.0    107.0
                  Petit        1137  1229.2*    92.2      (* all-140 labels)

              The five buttons alone are 736.7px at Très grand; the header's own
              chevron, title and three buttons already spend 348.7 of its 790.
              There is no text size at which they fit on one line — not even
              Petit, the widest row this app ever has. `.csSecHdr` is
              `flex-wrap: wrap`, so the result would not be a squeeze but a
              second line inside the header: the same displaced row the ruling
              set out to remove, moved twelve pixels up.

              THE FALLBACK, NAMED THERE AND RULED IN 149 §3, is this row: the
              bar reserves its own height, in flow, hidden-not-unmounted — and
              NOT the fixed 26.6px the 148 note floated, because that number
              was measured at one text size and a fixed px inside the zoomed
              root is the defect family of 142 §1 / 147 §1.3 / 148 §3. The
              buttons below render in every state so the row is never empty;
              the container's visibility is the LOCK's since 213B §20 [RULED
              48] (it was the selection's): a selection flips each button
              between dimmed-and-inert and lit-with-its-count. */}
              {/* 144 §4 — bulk edit. 145 §1.4, RULED: THE SELECTION SURVIVES.
                  It used to be cleared right here, at the OPEN — not at the
                  save — which is why a bulk edit left its own rows unticked and
                  a user correcting a mistake had to remember which ones they
                  had touched. The applyMark/onProForma convention does not
                  apply: those finish in one click, this hands off to a modal
                  that may be cancelled, and the rows a bulk edit SKIPS on a
                  provenance collision are exactly the ones still needing
                  attention. So nothing is cleared and nothing is filtered.
                  Delete still clears, in its onConfirm — a deleted row cannot
                  stay ticked. */}
              {canBulkEdit && onBulkEdit && (
                <button
                  className="btn"
                  disabled={noSel}
                  aria-disabled={noSel}
                  style={{ padding: "2px 9px", fontSize: FS.s10, background: "var(--as)", color: "var(--ac)", border: "1px solid var(--bd)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, ...bulkDim(noSel) }}
                  title={t("te.bulk.edit_tip", { fallback: "Edit the selected entries at once — empty fields stay unchanged" })}
                  onClick={() => { if (!noSel) onBulkEdit([...sel], bulkEditDone); }}
                >
                  <Pencil size={12} />
                  {t("te.bulk.edit", { fallback: "Edit selected" })}{selCount}
                </button>
              )}
              {canMark && (
                <>
                  <button className="btn" disabled={noSel} aria-disabled={noSel} style={{ padding: "2px 7px", fontSize: FS.s10, background: "rgba(251,191,36,.12)", color: "var(--am)", border: "1px solid rgba(251,191,36,.35)", ...bulkDim(noSel) }} onClick={() => { if (!noSel) applyMark("billable"); }}>
                    {t("te.bulk.mark_billable", { fallback: "Mark as billable" })}{selCount}
                  </button>
                  <button className="btn" disabled={noSel} aria-disabled={noSel} style={{ padding: "2px 7px", fontSize: FS.s10, background: "rgba(248,113,113,.10)", color: "var(--rd)", border: "1px solid rgba(248,113,113,.35)", ...bulkDim(noSel) }} onClick={() => { if (!noSel) applyMark("unbillable"); }}>
                    {t("te.bulk.mark_unbillable", { fallback: "Mark as unbillable" })}{selCount}
                  </button>
                  <button className="btn" disabled={noSel} aria-disabled={noSel} style={{ padding: "2px 7px", fontSize: FS.s10, background: "rgba(52,211,153,.12)", color: "var(--gn)", border: "1px solid rgba(52,211,153,.35)", ...bulkDim(noSel) }} onClick={() => { if (!noSel) applyMark("unmarked"); }}>
                    {t("te.bulk.unmark", { fallback: "Unmark" })}{selCount}
                  </button>
                </>
              )}
              {/* 144 §5 — bulk delete, last in the bar: destructive, apart. */}
              {canBulkDelete && confirmAndUndo && (
                <button
                  className="btn btn-danger"
                  disabled={noSel}
                  aria-disabled={noSel}
                  style={{ padding: "2px 9px", fontSize: FS.s10, display: "inline-flex", alignItems: "center", gap: 5, ...bulkDim(noSel) }}
                  title={t("te.bulk.delete_tip", { fallback: "Hard-delete the selected entries — a 30-second undo is the safety net" })}
                  onClick={() => { if (!noSel) bulkDelete(); }}
                >
                  <Trash2 size={12} />
                  {t("te.bulk.delete", { fallback: "Delete selected" })}{selCount}
                </button>
              )}
      </div>
      )}

      {/* ── 151C §1 — THE FIFTH ROW (Invoicing): "Create pro forma" stuck FAR
          LEFT, the two dedup buttons stuck FAR RIGHT, all unlocked only. The
          pro forma still ACTS on the billable selection (Phase 35's
          contract); with nothing picked it says so instead of hiding — this
          row's presence is the lock's, not the selection's, so no height
          reservation is owed ("lock/unlock may jump"). Dedups stay
          presence-driven for any host that hands onDoubles. */}
      {!locked && (onDoubles || (proFormaRow && canDelegate && onProForma)) && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {proFormaRow && canDelegate && onProForma && (
            <button
              className="btn"
              style={{ padding: "2px 9px", fontSize: FS.s10, background: "var(--as)", color: "var(--ac)", border: "1px solid var(--bd)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}
              title={t("te.bulk.invoice_tip", { fallback: "Create a pro forma invoice from the selected billable entries" })}
              onClick={() => {
                if (!billableSel.length) {
                  // 160 §5 — IT SAYS WHAT THE CONDITION IS, AND IT SAYS NO IN
                  // RED. The old text named "the current view", but the guard
                  // is `billableSel` — selected AND marked billable AND not yet
                  // invoiced (:1041) — so a user with a full view and nothing
                  // ticked was told to look at the wrong thing. And it was a
                  // GREEN toast: the success colour on a refusal.
                  toast && toast(t("invoice.toast.mark_and_select", {
                    fallback: "Mark time entries as billable and select them before creating an invoice",
                  }), "error");
                  return;
                }
                onProForma(billableSel.map((e: any) => e.id));
                setSel(new Set());
              }}
            >
              <FileText size={12} />
              {t("te.bulk.invoice_activities", { fallback: "Invoice activities" })}
              {billableSel.length > 0 && <> ({billableSel.length} · {fmtHM(billableSelMin)})</>}
            </button>
          )}
          {/* 161 §4 — THE SECOND BUTTON: a retainer is a document with no
              entries behind it (R1), so this one ignores the selection
              entirely — never disabled by it, no count suffix. Same gate as
              its neighbour (proFormaRow + delegate + onProForma), same shell.
              The payload is the WIDENED onProForma shape: an object carrying
              docKind, where the activities button still sends the bare id
              array. */}
          {proFormaRow && canDelegate && onProForma && (
            <button
              className="btn"
              style={{ padding: "2px 9px", fontSize: FS.s10, background: "var(--as)", color: "var(--ac)", border: "1px solid var(--bd)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}
              title={t("te.bulk.invoice_retainer_tip", { fallback: "Invoice a lump-sum retainer on this matter — no time entries involved" })}
              onClick={() => onProForma({ entryIds: [], docKind: "retainer" })}
            >
              <FileText size={12} />
              {t("te.bulk.invoice_retainer", { fallback: "Invoice a retainer" })}
            </button>
          )}
          {onDoubles && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {/* 150 §1 — dedups are group (c) of the BLOCK, not an Invoicing
                  perk: presence-driven (the host hands onDoubles or not). */}
              <button className="btn btn-ghost" style={{ padding: "2px 7px", fontSize: FS.s10 }} onClick={() => onDoubles("in-case")}>
                {t("te.doubles.in_case", { fallback: "Doubles in this case" })}
              </button>
              <button className="btn btn-ghost" style={{ padding: "2px 7px", fontSize: FS.s10 }} onClick={() => onDoubles("cross-case")}>
                {t("te.doubles.cross_case", { fallback: "Doubles with other cases" })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── 151C §2 — THE SCROLL WINDOW. Unlocked, the entry rows scroll in a
          window between the filter rows and the counter bar, so filters,
          entries and counters stay in sight while interacting; locked, this
          div styles nothing and the section flows in the page as before. The
          height: the app's own viewport conversion (100dvh / --ui-zoom —
          CallWindow/Combobox's idiom; a bare dvh would be VISUAL px inside
          the zoomed root, the 142 §1 family) minus a layout-px allowance for
          the rows above and the bar below (~270 at Moyen). The allowance is
          approximate BY DESIGN and degrades gracefully both ways: too tall
          and the sticky bar still pins to the viewport bottom; too short and
          a long list gives a few px back to the page. The 240 floor keeps
          the window usable on short viewports. (iv) the modals' scroll-hint
          pair rides INSIDE this scroller as its first and last children —
          sticky, zero-height, the idiom's own contract. */}
      <div
        ref={teWin.ref}
        style={
          !windowed
            ? undefined
            : {
                overflow: "auto",
                /* 151D §1(f) — measured (the effect above); the calc is only
                   the first paint's stand-in until the measurement lands. */
                maxHeight: winMax != null ? winMax : "max(240px, calc(100dvh / var(--ui-zoom, 1) - 270px))",
              }
        }
      >
        {windowed && teWin.above && <ScrollHint dir="up" inset={hintInsets.up} />}
      {/* 177 §1b — `teTable-page` says WHICH SCROLLER the pinned header answers
          to. Unwindowed, the table owns none and the header sticks inside
          `.content`, whose 14px top padding Chromium rests it short of — the
          strip Laurent's rows show through. Windowed, this scroller has no
          padding and the same cancellation would ride the header 14px above
          its own top edge, clipped (measured both ways,
          `drive-177-sticky-header.js`). The predicate is `windowed` itself,
          not a second spelling of it. */}
      <Table ref={tableRef} className={windowed ? "teTable" : "teTable teTable-page"} style={{ width: "100%" }}>
        <thead>
          <tr>
            {/* 146 §5 — RULED sortable: DATE, QUI, TYPE, TEMPS, TAUX, MONTANT.
                DESCRIPTION stays a plain `<th>`: a description sort answers no
                question anybody asks.

                158 §7 CORRECTED — DESCRIPTION FOLLOWS TYPE, and this table is
                the thing that moved. The ruling is
                date · collaborator · activity type · description, and this
                header used to put TEMPS between the last two, so a compact row
                obeying the ruling and a compact row obeying the header could
                not both be right. The header was the one that was wrong. Every
                other surface now matches it BY CONSTRUCTION rather than by a
                reconciliation each, and the sortable set is untouched — only
                the order the columns stand in changed. */}
            {sortTh("date", t("common.date", { fallback: "Date" }))}
            {sortTh("who", whoHeader)}
            {sortTh("type", t("common.type", { fallback: "Type" }))}
            <th>{t("te.col.desc", { fallback: "Description" })}</th>
            {sortTh("min", t("te.col.time", { fallback: "Time" }))}
            {/* 190 round 9 [AU] [RULED, Laurent 2026-09-17 — "now that the strip
                is real (hourly rate + costs) the time entries seem to lie"]:
                TAUX becomes TAUX ET FRAIS (a NEW key, 105's rule; `te.col.rate`
                retired) and reads the rate AND the matter's costs ratio; MONTANT
                becomes the fee grossed by that ratio — the served `wc`, the same
                per-row number the strip's left four sum, so the rows and the
                counter above them close to the cent. One component renders these
                two cells on the three surfaces (the CV/CCV time section, the
                Invoices section, the Dashboard) — measured: no second renderer. */}
            {showRate && sortTh("rate", t("te.col.rate_costs", { fallback: "Rate and costs" }))}
            {/* 190 round 11 [BC] [RULED, Laurent 2026-09-17]: the MONTANT header names
                its basis — "Montant hors TVA" — a NEW key (105's rule; `te.col.amt`
                retired); the cell stays exactly what [AU] made it, the fee grossed by
                the matter's costs ratio, EX-VAT (50,00 → 59,50), which is what makes a
                tab's rows sum to its counter to the cent. This does not contradict
                round 11's [AY]: that removed a SECOND LINE under a header, which broke
                the header row's baseline; this is a longer single-line header word,
                what every other header already is — one line, one baseline. */}
            {(showAmt || showAmount) && sortTh("amt", t("te.col.amt_ex_vat", { fallback: "Amount without VAT" }))}
            {/* 150 §2 (f) — the select header exists only when there is
                something a selection can do: (b) or (c), on unlock. */}
            <th style={{ whiteSpace: "nowrap" }}>
              {showSelect && (
                <>
                  {t("te.col.select", { fallback: "Select" })}
                  <input type="checkbox" style={{ marginLeft: 6, verticalAlign: "middle" }} title={t("te.select_all", { fallback: "Select all" })} checked={allVisibleSelected} onChange={(ev) => toggleAll(ev.target.checked)} />
                </>
              )}
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((e: any) => {
            const ef = feeCalc ? feeCalc[e.id] : null;
            const isSel = sel.has(e.id);
            const c = co.find((x: any) => x.id === e.coId);
            const cOf = caseOf(e);
            const ratio = cOf?.caseCollaborators?.find((x: any) => x.coId === e.coId)?.ratio ?? c?.defaultRatio ?? 100;
            return (
              /* 166 §H.3 — the edit modal's rate placeholder is the DERIVED
                 rate, and the server is the only thing that knows it (the
                 browser's effR resolver was removed in P17.1a F1). It is
                 already in hand here, per row, so it rides the payload rather
                 than being asked for again. */
              <tr key={e.id} onClick={() => { if (!e.invoiceId) onEdit && onEdit({ ...e, _derivedRate: ef?.derived ?? null }); }} style={{ ...(isSel ? { background: "var(--as)" } : {}), ...(e.invoiceId ? { opacity: DIMMED_ROW_OPACITY } : {}) /* 195B §C [RULED C.0] — 195 §A.4 named this row as a reader of the one value and it still carried the literal; measured, corrected */ }}>
                <td className="mono">{fd(e.date)}</td>
                <td><span className="name-cell">{c ? pname(c) : "—"}</span></td>
                <td>{typeCell(e)}</td>
                {/* 158 §7 — the body follows the header: DESCRIPTION, then TEMPS. */}
                <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.desc || ""}>{e.desc || "—"}</td>
                <td className="mono">{fmin(e.min)}</td>
                {showRate && (
                  /* ── 166 §H.4 — A STATED RATE WEARS THE STATED COLOUR ───────
                     Laurent's ruling 9, 2026-09-04: the app did not compute
                     this number, so it renders in `--st` — the same token as a
                     stated retainer due (164 §A.7) and an imported activity
                     code (§B). THE TEST IS THE SERVER'S: `ef.stated` is
                     `frozenRate(e) != null` asked inside `entryFees.js`, not a
                     second spelling of the predicate over here.
                     AND THE RATIO SUFFIX GOES with it: `({ratio}%)` describes
                     the DERIVATION — referenceHourlyRate × the collaborator's
                     ratio — and a stated rate has no derivation to describe,
                     so the percentage would be a claim about arithmetic that
                     did not happen. */
                  <td className="mono" style={{ whiteSpace: "nowrap", ...(ef?.stated ? { color: "var(--st)" } : {}) }}
                      title={ef?.stated ? t("te.rate.stated_tip", { fallback: "Stated rate — set by hand, does not follow the case's hourly rate" }) : undefined}>
                    {fq(ef ? ef.er : null)} €/h{!ef?.stated && cOf?.referenceHourlyRate != null && <span style={{ fontSize: FS.s9, color: "var(--tm)", marginLeft: 3 }}>({ratio}%)</span>}
                    {/* [AU] — "+ 19 %": the matter's costs ratio, served beside the row (`cr`) */}
                    {ef && typeof ef.cr === "number" && <span style={{ marginLeft: 4 }}>+ {ef.cr} %</span>}
                  </td>
                )}
                {(showAmt || showAmount) && (
                  /* [AU] — the amount is fees + costs (`wc`, ex-VAT), never divided or
                     grossed here: the server's per-row number, `costsAug` over the row. */
                  <td className="mono">{ef && ef.er > 0 && typeof ef.wc === "number" ? fc(ef.wc) : "—"}</td>
                )}
                <td onClick={(ev) => ev.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                  {showPill && pill(e)}
                  {showSelect && (
                    <input
                      type="checkbox"
                      disabled={!!e.invoiceId}
                      title={e.invoiceId ? t("te.invoiced_tip", { fallback: "Already invoiced — cancel the invoice to correct" }) : undefined}
                      style={{ marginLeft: 6, verticalAlign: "middle", cursor: e.invoiceId ? "not-allowed" : "pointer" }}
                      checked={isSel}
                      onChange={(ev) => { if (!e.invoiceId) toggleRow(e.id, ev.target.checked); }}
                    />
                  )}
                </td>
                {/* Phase 35 Stage-3 r2 (§3) — invoiced rows: Edit/Delete are
                  disabled (and pointer-events:none). Native title is suppressed
                  on disabled controls — it failed to show twice — so the reason
                  appears via a real CSS hover element (.teLockTip) anchored to
                  this cell (.teLockCell), shown on cell hover. The string is
                  guaranteed non-empty (seeded key, else INVOICED_TIP_EN). */}
                <td onClick={(ev) => ev.stopPropagation()} className={e.invoiceId ? "teLockCell" : undefined} style={{ whiteSpace: "nowrap", cursor: e.invoiceId ? "not-allowed" : undefined }}>
                  {!locked && (
                    <>
                      {e.invoiceId && (
                        <span className="teLockTip">
                          {t("te.invoiced_tip", { fallback: INVOICED_TIP_EN }) || INVOICED_TIP_EN}
                        </span>
                      )}
                      <button className="btn btn-ghost" disabled={!!e.invoiceId} title={e.invoiceId ? undefined : t("common.edit", { fallback: "Edit" })} style={{ padding: "1px 4px", fontSize: FS.s9, marginRight: 3, opacity: e.invoiceId ? 0.4 : 1, cursor: e.invoiceId ? "not-allowed" : "pointer", pointerEvents: e.invoiceId ? "none" : undefined }} onClick={(ev) => { ev.stopPropagation(); if (!e.invoiceId) onEdit && onEdit({ ...e, _derivedRate: ef?.derived ?? null }); }}>
                        <Pencil size={14} />
                      </button>
                      {/* 152 §2 — ARCHIVE, between edit and the bin, wearing
                          the invoiced lock's own face (disabled + dimmed +
                          pointer-events off, the .teLockTip above explaining
                          it) rather than a third face invented for one
                          button. Reversible: the row leaves every list and
                          every counter and waits in the Archive view.
                          The list corrects itself through the app's own
                          stream — the route pokes `te`, and App.tsx:2863
                          debounces a refetch 600ms later — so no host needs
                          a new handler and all four surfaces gain the
                          button from this one edit. */}
                      <button className="btn btn-ghost" disabled={!!e.invoiceId} title={e.invoiceId ? undefined : t("common.archive", { fallback: "Archive" })} style={{ padding: "1px 4px", fontSize: FS.s9, marginRight: 3, color: "var(--or)", opacity: e.invoiceId ? 0.4 : 1, cursor: e.invoiceId ? "not-allowed" : "pointer", pointerEvents: e.invoiceId ? "none" : undefined }} onClick={async (ev) => { ev.stopPropagation(); if (e.invoiceId) return; try { await archiveTimeEntry(e.id); toast && toast(t("toast.archived", { fallback: "Archived" })); } catch (err: any) { toast && toast(err?.message || t("common.failed", { fallback: "Failed" }), "error"); /* 166 §G.3 */ } }}>
                        <Archive size={14} />
                      </button>
                      <button className="btn btn-danger" disabled={!!e.invoiceId} title={e.invoiceId ? undefined : t("common.delete", { fallback: "Delete" })} style={{ padding: "1px 4px", fontSize: FS.s9, opacity: e.invoiceId ? 0.4 : 1, cursor: e.invoiceId ? "not-allowed" : "pointer", pointerEvents: e.invoiceId ? "none" : undefined }} onClick={(ev) => { ev.stopPropagation(); if (!e.invoiceId) onDelete && onDelete(e); }}>
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {visible.length === 0 && (
            <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--tm)", padding: 12 }}>{t("te.none", { fallback: "No entries" })}</td></tr>
          )}
        </tbody>
      </Table>
      {/* 151C §2 (iii) — the host's tail (Invoicing's pro forma list) follows
          the entries INSIDE the window; reaching it = scroll to bottom. */}
      {afterTable}
        {windowed && teWin.below && <ScrollHint dir="down" inset={hintInsets.down} />}
      {/* ── 151D §2(c)(e) → 190 round 6 [W] → round 8 [AK] — THE COUNTERS ARE
          THE PAGE'S FLOOR: CountersStrip.tsx (one component, three hosts)
          portals the plate into App's footer slot under the page scroller,
          so it is pinned at the viewport's bottom WHENEVER it is shown — a
          short list, a long one, mid-page or at the page's end. It is
          declared here, inside the window, because THIS is where the host
          decides whether it shows (unlocked only — 151C's overrule of
          150 §2 — EXCEPT where the host says otherwise, 158 §3) and what it
          reads (`figures`, `counterBg` the plate's colour per surface); the
          DOM it lands in is the slot's. What `windowed` still gates, and
          why: the scroller (151C §2 — filters, rows and counters in sight
          while the rows scroll in a capped window) and its two hints — both
          still earn their place; the sticky floor no longer exists here. */}
      {(countersAlwaysVisible || !locked) && figures && (
        <CountersStrip host={stripHost} figures={figures} background={counterBg} t={t as TFn} />
      )}
      {/* Phase 34 (Stage 3 r2) — anti-jump spacer; height managed by onTabChange
          + the layout effect above. Usually 0 (no visible gap).
          190 round 7 [AE] — AFTER the floor, not before it. When the page IS
          at its bottom the reservation is real (Phase 34's contract: the page
          may not shrink under the viewport, or the tabs just clicked jump
          away by the shrink), and a reservation that stood BETWEEN the rows
          and the strip read as a hole above a pinned floor. Under the strip
          it is what it always was before 151C windowed this section: slack at
          the end. The strip stays the window's sticky floor — `bottom: 0`
          pins it while the rows overflow, and a positive spacer exists only
          while they do not (the deficit is bounded by the window's shrink), so
          the two never meet. */}
      <div aria-hidden style={{ height: spacer }} />
      </div>
    </div>
  );
}
